/**
 * @zendir/ui - Domain-Specific Chart Components
 *
 * Pre-configured charts for common space operations use cases.
 * These wrap AstroChart with domain-specific defaults and data transformations.
 */

import React, { forwardRef, memo, useMemo, useState, useEffect } from "react";
import { AstroChart, AstroChartHandle } from "./AstroChart";
import { useTheme } from "../../theme";
import type {
  SeriesData,
  PowerChartData,
  AttitudeChartData,
  ThermalChartData,
  LinkBudgetChartData,
  ContactWindowData,
  SpectrumDataPoint,
  ManeuverBudgetData,
  EclipseTimelineData,
  AnnotationOptions,
  ExportOptions,
  InfoTooltipOptions,
  CoverageGridData,
} from "./types";

// echarts-gl: loaded once, state tracked so 3D components don't render before GL registers.
let _echartsGlReady = false;
let _echartsGlPromise: Promise<boolean> | null = null;

function _loadEchartsGl(): Promise<boolean> {
  if (!_echartsGlPromise) {
    _echartsGlPromise = import("echarts-gl" as string) // cast suppresses "no types" warning
      .then(() => {
        _echartsGlReady = true;
        return true;
      })
      .catch((err) => {
        console.warn(
          "[zendir-ui] echarts-gl failed to load — 3D charts will not render:",
          err,
        );
        return false;
      });
  }
  return _echartsGlPromise;
}

// Start loading immediately when the module is imported (same as before, but tracked).
_loadEchartsGl();

/** Returns true once echarts-gl has registered its series types with ECharts. */
function useEchartsGlReady(): boolean {
  const [ready, setReady] = useState(_echartsGlReady);
  useEffect(() => {
    if (!_echartsGlReady) {
      _loadEchartsGl().then((ok) => {
        if (ok) setReady(true);
      });
    }
  }, []);
  return ready;
}

// Common props shared by all domain charts
interface CommonChartProps {
  /** Export options */
  export?: ExportOptions;
  /** Info tooltip */
  infoTooltip?: InfoTooltipOptions;

  // === Header Status Icon System (inherited from AstroChartProps) ===

  /**
   * Icon name to display in chart header (uses AstroIcon)
   * Shows before the chart title
   */
  icon?: string;
  /**
   * Status for the header icon (shows as colored dot)
   * Uses AstroUXDS status colors
   */
  iconStatus?:
    | "off"
    | "standby"
    | "normal"
    | "caution"
    | "serious"
    | "critical";
  /**
   * Status message shown on hover over the status icon
   */
  statusMessage?: string;
}
import {
  STATUS_COLORS,
  ASTRO_DATA_VIZ_COLORS,
  ZENDIR_BRAND_COLORS,
} from "./theme";

// Medium-soft status colors (readable, not too pale)
const SOFT_STATUS_COLORS = {
  normal: "#6ed86e",
  caution: "#ffd54f",
  serious: "#ffa726",
  critical: "#e57373",
  off: "#90a4ae",
  standby: "#4fc3f7",
};

// =============================================================================
// Theme-Aware Tooltip & Legend Styles (for 3D charts)
// =============================================================================

/**
 * Get theme-aware tooltip style for 3D charts
 * 3D charts don't use the standard AstroChart theming
 */
function useChartTooltipStyle() {
  const { tokens, mode } = useTheme();
  const isDark = mode === "dark";

  return {
    backgroundColor: isDark
      ? "rgba(15, 23, 42, 0.95)"
      : "rgba(255, 255, 255, 0.95)",
    borderColor: isDark
      ? "rgba(100, 116, 139, 0.4)"
      : "rgba(100, 116, 139, 0.3)",
    textStyle: {
      color: tokens.colors.text.primary,
      fontSize: 12,
    },
  };
}

/**
 * Get theme-aware legend style for 3D charts
 */
function useChartLegendStyle() {
  const { tokens } = useTheme();
  return {
    textStyle: {
      color: tokens.colors.text.secondary,
    },
  };
}

/**
 * Get theme-aware visual map style for 3D charts
 * Includes hoverLink for highlighting hovered regions (Google/Apple level UX)
 */
function useChartVisualMapStyle() {
  const { tokens } = useTheme();
  return {
    textStyle: {
      color: tokens.colors.text.tertiary,
    },
    // Allow drag selection on the visualMap bar
    calculable: true,
    // Enable hover linking - when hovering on visualMap, highlight only that range
    hoverLink: true,
    // Non-hovered regions get faded opacity (not invisible, just light)
    outOfRange: {
      color: "rgba(128, 128, 128, 0.12)", // Very light gray with low opacity
      opacity: 0.15, // Also reduce opacity for 3D elements
    },
    // Handle styles for the interactive bar (the draggable endpoints)
    handleStyle: {
      borderColor: tokens.colors.accent.primary,
      color: tokens.colors.accent.primary,
    },
    // Indicator style when hovering
    indicatorStyle: {
      borderColor: "#fff",
    },
    // Smooth transition when highlighting
    realtime: true,
  };
}

// =============================================================================
// Power Chart
// =============================================================================

export interface PowerChartProps extends CommonChartProps {
  /** Power data points (time in ms) */
  data: PowerChartData[];
  /** Chart title */
  title?: string;
  /** Show legend */
  showLegend?: boolean;
  /** Width */
  width?: number | string;
  /** Height */
  height?: number;
  /** Show eclipse shading */
  showEclipse?: boolean;
  /** Loading state */
  loading?: boolean;
  /** Enable zoom */
  enableZoom?: boolean;
  /** Custom className */
  className?: string;
}

export const PowerChart = memo(
  forwardRef<AstroChartHandle, PowerChartProps>(function PowerChart(
    {
      data = [],
      title = "Power system",
      showLegend = true,
      width = "100%",
      height = 250,
      showEclipse = true,
      loading = false,
      enableZoom = false,
      className = "",
      export: exportOptions,
      infoTooltip,
      // Icon/status props
      icon,
      iconStatus,
      statusMessage,
    },
    ref,
  ) {
    // Transform data to series
    const { series, annotations } = useMemo(() => {
      const solarSeries: SeriesData = {
        id: "solar",
        name: "Solar",
        type: "line",
        data: data.map((d) => ({ x: d.time, y: d.solarPower })),
        color: SOFT_STATUS_COLORS.standby, // Cyan for solar
        yAxisIndex: 0,
        smooth: true,
        symbol: { type: "none" },
      };

      const batterySeries: SeriesData = {
        id: "battery",
        name: "Battery",
        type: "line",
        data: data.map((d) => ({ x: d.time, y: d.batteryPercent })),
        color: SOFT_STATUS_COLORS.normal, // Green for battery
        yAxisIndex: 1,
        smooth: true,
        symbol: { type: "none" },
      };

      const netPowerSeries: SeriesData = {
        id: "netPower",
        name: "Net",
        type: "line",
        data: data.map((d) => ({ x: d.time, y: d.netPower })),
        color: ZENDIR_BRAND_COLORS.electric, // Zendir Electric
        yAxisIndex: 0,
        smooth: true,
        symbol: { type: "none" },
      };

      const consumptionSeries: SeriesData = {
        id: "consumption",
        name: "Load",
        type: "line",
        data: data.map((d) => ({ x: d.time, y: d.consumption })),
        color: SOFT_STATUS_COLORS.critical, // Red for consumption
        yAxisIndex: 0,
        lineStyle: { type: "dashed", width: 1.5 },
        smooth: true,
        symbol: { type: "none" },
      };

      // Eclipse shading as mark areas
      const eclipseAreas: AnnotationOptions["markAreas"] = { data: [] };
      if (showEclipse) {
        let inEclipse = false;
        let eclipseStart = 0;

        data.forEach((d, _i) => {
          if (d.eclipse && !inEclipse) {
            inEclipse = true;
            eclipseStart = d.time;
          } else if (!d.eclipse && inEclipse) {
            inEclipse = false;
            eclipseAreas.data.push([
              { xAxis: eclipseStart, name: "Eclipse" },
              { xAxis: d.time },
            ]);
          }
        });

        // Handle ongoing eclipse
        if (inEclipse && data.length > 0) {
          eclipseAreas.data.push([
            { xAxis: eclipseStart, name: "Eclipse" },
            { xAxis: data[data.length - 1].time },
          ]);
        }

        eclipseAreas.itemStyle = {
          color: `${STATUS_COLORS.critical}15`,
        };
      }

      return {
        series: [solarSeries, batterySeries, netPowerSeries, consumptionSeries],
        annotations:
          showEclipse && eclipseAreas.data.length > 0
            ? { markAreas: eclipseAreas }
            : undefined,
      };
    }, [data, showEclipse]);

    const tooltipConfig = useMemo(
      () => ({
        trigger: "axis" as const,
        crosshair: "x" as const,
        formatter: (params: unknown) => {
          const p = Array.isArray(params) ? params : [];
          if (p.length === 0) return "";
          const axisVal =
            (p[0] as { value?: unknown; axisValue?: unknown }).axisValue ??
            (p[0] as { value?: unknown }).value;
          const time =
            typeof axisVal === "number"
              ? axisVal
              : axisVal instanceof Date
                ? axisVal.getTime()
                : Number(axisVal);
          const inEclipse = annotations?.markAreas?.data
            ? annotations.markAreas.data.some((pair) => {
                const start = (pair[0].xAxis as number) ?? 0;
                const end = (pair[1].xAxis as number) ?? 0;
                return time >= start && time <= end;
              })
            : false;
          const lines = p.map(
            (item: { marker?: string; seriesName?: string; value?: unknown }) =>
              `${item.marker ?? ""} ${item.seriesName ?? ""}: ${item.value ?? ""}`,
          );
          if (inEclipse)
            lines.push("◐ Eclipse — spacecraft in shadow; solar panels off");
          return lines.join("<br/>");
        },
      }),
      [annotations],
    );

    return (
      <AstroChart
        ref={ref}
        type="line"
        title={title}
        series={series}
        width={width}
        height={height}
        loading={loading}
        className={className}
        icon={icon}
        iconStatus={iconStatus}
        statusMessage={statusMessage}
        xAxis={{ type: "time", name: "" }}
        yAxis={[
          {
            name: "Power [W]",
            position: "left",
            min: 0,
          },
          {
            name: "Battery [%]",
            position: "right",
            min: 0,
            max: 100,
          },
        ]}
        tooltip={tooltipConfig}
        legend={{ show: showLegend, position: "bottom" }}
        zoom={
          enableZoom ? { enabled: true, type: "inside", axis: "x" } : undefined
        }
        annotations={annotations}
        export={exportOptions}
        infoTooltip={infoTooltip}
      />
    );
  }),
);

// =============================================================================
// Attitude Chart
// =============================================================================

export interface AttitudeChartProps extends CommonChartProps {
  /** Attitude data points (time in ms) */
  data: AttitudeChartData[];
  /** Chart title */
  title?: string;
  /** Show legend */
  showLegend?: boolean;
  /** Width */
  width?: number | string;
  /** Height */
  height?: number;
  /** Loading state */
  loading?: boolean;
  /** Enable zoom */
  enableZoom?: boolean;
  /** Show pointing error */
  showError?: boolean;
  /** Show angular velocity */
  showAngularVelocity?: boolean;
  /** Custom className */
  className?: string;
}

export const AttitudeChart = memo(
  forwardRef<AstroChartHandle, AttitudeChartProps>(function AttitudeChart(
    {
      data = [],
      title = "Attitude control",
      showLegend = true,
      width = "100%",
      height = 200,
      loading = false,
      enableZoom = false,
      showError = true,
      showAngularVelocity = true,
      className = "",
      export: exportOptions,
      infoTooltip,
      // Icon/status props
      icon,
      iconStatus,
      statusMessage,
    },
    ref,
  ) {
    // Transform data to series
    const series = useMemo(() => {
      const result: SeriesData[] = [
        {
          id: "roll",
          name: "Roll",
          type: "line",
          data: data.map((d) => ({ x: d.time, y: d.roll })),
          color: SOFT_STATUS_COLORS.critical, // Red
          smooth: true,
          symbol: { type: "none" },
        },
        {
          id: "pitch",
          name: "Pitch",
          type: "line",
          data: data.map((d) => ({ x: d.time, y: d.pitch })),
          color: SOFT_STATUS_COLORS.normal, // Green
          smooth: true,
          symbol: { type: "none" },
        },
        {
          id: "yaw",
          name: "Yaw",
          type: "line",
          data: data.map((d) => ({ x: d.time, y: d.yaw })),
          color: ZENDIR_BRAND_COLORS.electric, // Zendir Electric
          smooth: true,
          symbol: { type: "none" },
        },
      ];

      if (showError && data.some((d) => d.pointingError !== undefined)) {
        result.push({
          id: "error",
          name: "Error",
          type: "line",
          data: data
            .filter((d) => d.pointingError !== undefined)
            .map((d) => ({ x: d.time, y: d.pointingError! })),
          color: SOFT_STATUS_COLORS.caution, // Yellow (status color)
          smooth: true,
          symbol: { type: "none" },
          lineStyle: { width: 1.5 },
        });
      }

      if (
        showAngularVelocity &&
        data.some((d) => d.angularVelocity !== undefined)
      ) {
        result.push({
          id: "angVel",
          name: "ω",
          type: "line",
          data: data
            .filter((d) => d.angularVelocity !== undefined)
            .map((d) => ({ x: d.time, y: d.angularVelocity! })),
          color: ZENDIR_BRAND_COLORS.purple, // Zendir Purple
          smooth: true,
          symbol: { type: "none" },
          lineStyle: { type: "dashed", width: 1 },
        });
      }

      return result;
    }, [data, showError, showAngularVelocity]);

    return (
      <AstroChart
        ref={ref}
        type="line"
        title={title}
        series={series}
        width={width}
        height={height}
        loading={loading}
        className={className}
        icon={icon}
        iconStatus={iconStatus}
        statusMessage={statusMessage}
        xAxis={{ type: "time", name: "" }}
        yAxis={{
          name: "Attitude [deg]",
          // Center zero line
        }}
        tooltip={{ trigger: "axis", crosshair: "x" }}
        legend={{ show: showLegend, position: "bottom" }}
        zoom={
          enableZoom ? { enabled: true, type: "inside", axis: "x" } : undefined
        }
        annotations={{
          markLines: {
            data: [
              { yAxis: 0, label: "", lineStyle: { type: "solid", width: 1 } },
            ],
          },
        }}
        export={exportOptions}
        infoTooltip={infoTooltip}
      />
    );
  }),
);

// =============================================================================
// Thermal Heatmap Chart
// =============================================================================

export interface ThermalHeatmapChartProps extends CommonChartProps {
  /** Thermal data */
  data: ThermalChartData[];
  /** Zone names */
  zones: string[];
  /** Chart title */
  title?: string;
  /** Width */
  width?: number | string;
  /** Height */
  height?: number;
  /** Temperature range */
  range?: [number, number];
  /** Loading state */
  loading?: boolean;
  /** Custom className */
  className?: string;
}

export const ThermalHeatmapChart = memo(
  forwardRef<AstroChartHandle, ThermalHeatmapChartProps>(
    function ThermalHeatmapChart(
      {
        data = [],
        zones = [],
        title = "Thermal zones",
        width = "100%",
        height = 300,
        range = [-40, 80],
        loading = false,
        className = "",
        export: exportOptions,
        infoTooltip,
      },
      ref,
    ) {
      // Transform to heatmap format
      const { series, timeLabels } = useMemo(() => {
        const timeLabels = data.map((d) =>
          new Date(d.time).toLocaleTimeString([], {
            hour: "2-digit",
            minute: "2-digit",
            hour12: false,
          }),
        );

        // Build heatmap data: [x, y, value]
        const heatmapData: [number, number, number][] = [];
        data.forEach((d, timeIdx) => {
          zones.forEach((zone, zoneIdx) => {
            const temp = d.zones[zone] ?? 0;
            heatmapData.push([timeIdx, zoneIdx, temp]);
          });
        });

        return {
          series: [
            {
              id: "thermal",
              name: "Temperature",
              type: "heatmap" as const,
              data: heatmapData,
            },
          ],
          timeLabels,
        };
      }, [data, zones]);

      return (
        <AstroChart
          ref={ref}
          type="heatmap"
          title={title}
          series={series}
          width={width}
          height={height}
          loading={loading}
          className={className}
          xAxis={{ type: "category" }}
          yAxis={{ type: "category" }}
          tooltip={{ trigger: "item" }}
          echartsOptions={{
            xAxis: { data: timeLabels },
            yAxis: { data: zones },
            visualMap: {
              min: range[0],
              max: range[1],
              calculable: true,
              orient: "vertical",
              right: 10,
              top: "center",
              // Enable hover linking - highlight only hovered range
              hoverLink: true,
              // Non-hovered regions get faded opacity
              outOfRange: {
                color: "rgba(128, 128, 128, 0.12)",
                opacity: 0.15,
              },
              // Thermal gradient (cold → hot) - Original high-contrast palette
              // Multi-hue gradient for clear temperature differentiation
              inRange: {
                color: [
                  "#313695", // Deep blue (very cold)
                  "#4575b4", // Blue
                  "#74add1", // Light blue
                  "#abd9e9", // Pale blue
                  "#e0f3f8", // Very pale blue
                  "#ffffbf", // Pale yellow (neutral)
                  "#fee090", // Light orange
                  "#fdae61", // Orange
                  "#f46d43", // Red-orange
                  "#d73027", // Red
                  "#a50026", // Dark red (very hot)
                ],
              },
            },
            series: [
              {
                type: "heatmap",
                data: series[0].data,
                label: {
                  show: true,
                  formatter: (p: { value?: unknown }) =>
                    Array.isArray(p.value) && typeof p.value[2] === "number"
                      ? `${p.value[2]}°`
                      : "",
                },
                emphasis: {
                  itemStyle: {
                    shadowBlur: 10,
                    shadowColor: "rgba(0, 0, 0, 0.5)",
                  },
                },
              },
            ],
          }}
          export={exportOptions}
          infoTooltip={infoTooltip}
        />
      );
    },
  ),
);

// =============================================================================
// Link Budget Chart
// =============================================================================

export interface LinkBudgetChartProps extends CommonChartProps {
  /** Link budget data */
  data: LinkBudgetChartData[];
  /** Chart title */
  title?: string;
  /** Show legend */
  showLegend?: boolean;
  /** Width */
  width?: number | string;
  /** Height */
  height?: number;
  /** Margin threshold (dB) */
  marginThreshold?: number;
  /** Loading state */
  loading?: boolean;
  /** Custom className */
  className?: string;
}

export const LinkBudgetChart = memo(
  forwardRef<AstroChartHandle, LinkBudgetChartProps>(function LinkBudgetChart(
    {
      data = [],
      title = "Link budget",
      showLegend = true,
      width = "100%",
      height = 200,
      marginThreshold = 3,
      loading = false,
      className = "",
      export: exportOptions,
      infoTooltip,
    },
    ref,
  ) {
    const series = useMemo(
      (): SeriesData[] => [
        {
          id: "signal",
          name: "Signal",
          type: "area",
          data: data.map((d) => ({ x: d.time, y: d.signalStrength })),
          color: SOFT_STATUS_COLORS.normal,
          smooth: true,
          symbol: { type: "none" },
          areaStyle: { opacity: 0.2 },
        },
        {
          id: "noise",
          name: "Noise floor",
          type: "line",
          data: data.map((d) => ({ x: d.time, y: d.noiseFloor })),
          color: SOFT_STATUS_COLORS.critical,
          smooth: true,
          symbol: { type: "none" },
          lineStyle: { type: "dashed", width: 1 },
        },
        {
          id: "margin",
          name: "Margin",
          type: "line",
          data: data.map((d) => ({ x: d.time, y: d.margin })),
          color: SOFT_STATUS_COLORS.standby,
          yAxisIndex: 1,
          smooth: true,
          symbol: { type: "none" },
        },
      ],
      [data],
    );

    return (
      <AstroChart
        ref={ref}
        type="line"
        title={title}
        series={series}
        width={width}
        height={height}
        loading={loading}
        className={className}
        xAxis={{ type: "time" }}
        yAxis={[
          { name: "dBm", position: "left" },
          { name: "Margin [dB]", position: "right", min: 0 },
        ]}
        tooltip={{ trigger: "axis", crosshair: "x" }}
        legend={{ show: showLegend, position: "bottom" }}
        annotations={{
          markLines: {
            data: [
              {
                yAxis: marginThreshold,
                label: `Min margin (${marginThreshold} dB)`,
                color: SOFT_STATUS_COLORS.caution,
                lineStyle: { type: "dashed" },
              },
            ],
          },
        }}
        export={exportOptions}
        infoTooltip={infoTooltip}
      />
    );
  }),
);

// =============================================================================
// Subsystem Status Gauge
// =============================================================================

export interface SubsystemGaugeProps extends CommonChartProps {
  /** Current value (0-100) */
  value: number;
  /** Subsystem name */
  name: string;
  /** Unit label */
  unit?: string;
  /** Width */
  width?: number | string;
  /** Height */
  height?: number;
  /** Status thresholds */
  thresholds?: {
    caution?: number;
    critical?: number;
  };
  /** Loading state */
  loading?: boolean;
  /** Custom className */
  className?: string;
}

export const SubsystemGauge = memo(
  forwardRef<AstroChartHandle, SubsystemGaugeProps>(function SubsystemGauge(
    {
      value,
      name,
      unit = "%",
      width = 200,
      height = 200,
      thresholds = { caution: 70, critical: 90 },
      loading = false,
      className = "",
      export: exportOptions,
      infoTooltip,
    },
    ref,
  ) {
    const clampedValue = Math.max(0, Math.min(100, value));
    const widthPx = typeof width === "number" ? width : height;
    const gaugeSize = Math.max(110, Math.min(widthPx, height));
    const arcWidth = Math.max(8, Math.round(gaugeSize * 0.085));
    const valueFontSize = Math.max(18, Math.round(gaugeSize * 0.17));
    const titleFontSize = Math.max(10, Math.round(gaugeSize * 0.085));
    const compactGauge = gaugeSize < 150;
    const centerY = compactGauge ? "53%" : "55%";
    const detailY = compactGauge ? "10%" : "15%";
    const titleY = compactGauge ? "78%" : "84%";
    const unitSuffix = unit?.trim() ?? "";
    const longUnit = unitSuffix.length > 4;

    // Modern color based on value thresholds
    const getValueColor = () => {
      if (thresholds.critical && clampedValue >= thresholds.critical)
        return SOFT_STATUS_COLORS.critical;
      if (thresholds.caution && clampedValue >= thresholds.caution)
        return SOFT_STATUS_COLORS.caution;
      return SOFT_STATUS_COLORS.normal;
    };

    return (
      <AstroChart
        ref={ref}
        type="gauge"
        series={[]} // Empty - we provide full gauge config via echartsOptions
        width={width}
        height={height}
        loading={loading}
        className={className}
        echartsOptions={{
          series: [
            {
              type: "gauge",
              min: 0,
              max: 100,
              startAngle: 200,
              endAngle: -20,
              radius: "90%",
              center: ["50%", centerY],
              // Modern minimal track
              axisLine: {
                lineStyle: {
                  width: arcWidth,
                  color: [[1, "rgba(100, 116, 139, 0.15)"]], // Subtle track
                },
              },
              // Hide all traditional gauge elements
              axisTick: { show: false },
              splitLine: { show: false },
              axisLabel: { show: false },
              pointer: { show: false }, // No pointer for modern look
              // Progress arc with gradient
              progress: {
                show: true,
                width: arcWidth,
                roundCap: true,
                itemStyle: {
                  color: {
                    type: "linear",
                    x: 0,
                    y: 0,
                    x2: 1,
                    y2: 0,
                    colorStops: [
                      { offset: 0, color: "rgba(100, 200, 200, 0.6)" },
                      { offset: 1, color: getValueColor() },
                    ],
                  },
                  shadowColor: getValueColor(),
                  shadowBlur: 8,
                },
              },
              // Clean title below
              title: {
                show: true,
                offsetCenter: [0, titleY],
                fontSize: titleFontSize,
                fontWeight: 500,
                color: "rgba(255, 255, 255, 0.7)",
              },
              // Modern centered value
              detail: {
                valueAnimation: true,
                formatter: (currentValue: number) => {
                  const rounded = Math.round(currentValue);
                  if (!unitSuffix) return `${rounded}`;
                  if (longUnit) return `${rounded}\n{unit|${unitSuffix}}`;
                  return `${rounded}${unitSuffix}`;
                },
                fontSize: valueFontSize,
                fontWeight: 700, // AstroUXDS bold for gauge values
                lineHeight: Math.round(valueFontSize * 1.05),
                offsetCenter: [0, detailY],
                color: getValueColor(),
                rich: {
                  unit: {
                    color: "rgba(255, 255, 255, 0.75)",
                    fontSize: Math.max(10, Math.round(valueFontSize * 0.5)),
                    fontWeight: 500,
                    lineHeight: Math.round(valueFontSize * 0.8),
                  },
                },
              },
              data: [{ value: clampedValue, name }],
            },
          ],
        }}
        export={exportOptions}
        infoTooltip={infoTooltip}
      />
    );
  }),
);

// =============================================================================
// Orbit Visualization (Polar)
// =============================================================================

export interface OrbitChartProps extends CommonChartProps {
  /** Orbit position data (angle in degrees, altitude in km) */
  data: { angle: number; altitude: number; label?: string }[];
  /** Chart title */
  title?: string;
  /** Reference altitude (nominal orbit) */
  referenceAltitude?: number;
  /** Width */
  width?: number | string;
  /** Height */
  height?: number;
  /** Loading state */
  loading?: boolean;
  /** Custom className */
  className?: string;
}

export const OrbitChart = memo(
  forwardRef<AstroChartHandle, OrbitChartProps>(function OrbitChart(
    {
      data = [],
      title = "Polar Projection",
      referenceAltitude,
      width = 300,
      height = 300,
      loading = false,
      className = "",
      export: exportOptions,
      infoTooltip,
    },
    ref,
  ) {
    return (
      <AstroChart
        ref={ref}
        type="scatter"
        title={title}
        series={[]} // Empty - we provide polar series via echartsOptions
        width={width}
        height={height}
        loading={loading}
        className={className}
        echartsOptions={{
          polar: {
            radius: ["0%", "75%"],
            center: ["50%", "50%"],
          },
          angleAxis: {
            type: "value",
            min: 0,
            max: 360,
            startAngle: 90,
            interval: 30,
            axisLine: { show: false },
            axisTick: { show: false },
            splitLine: {
              show: true,
              lineStyle: {
                color: "rgba(100, 116, 139, 0.15)",
                type: "dashed",
              },
            },
            axisLabel: {
              fontSize: 9,
              color: "rgba(255, 255, 255, 0.4)",
              formatter: (value: number) => (value === 0 ? "" : `${value}°`),
            },
          },
          radiusAxis: {
            min: referenceAltitude ? referenceAltitude * 0.9 : "dataMin",
            max: referenceAltitude ? referenceAltitude * 1.1 : "dataMax",
            axisLine: { show: false },
            axisTick: { show: false },
            axisLabel: { show: false },
            splitLine: {
              show: true,
              lineStyle: {
                color: "rgba(100, 116, 139, 0.2)",
                type: "dashed",
              },
            },
          },
          series: [
            {
              type: "scatter",
              name: "Position",
              coordinateSystem: "polar",
              data: data.map((d) => [d.altitude, d.angle]),
              symbolSize: 8,
              itemStyle: {
                color: ZENDIR_BRAND_COLORS.electric, // Zendir Electric
                shadowColor: ZENDIR_BRAND_COLORS.electric,
                shadowBlur: 6,
              },
            },
          ],
        }}
        export={exportOptions}
        infoTooltip={infoTooltip}
      />
    );
  }),
);

// =============================================================================
// Contact Window / Pass Chart (Gantt-style) – AstroUXD compliant
// =============================================================================

export interface ContactWindowChartProps extends CommonChartProps {
  /** Contact window data by ground station */
  data: ContactWindowData[];
  /** Chart title */
  title?: string;
  /** Time range [start, end] in ms */
  timeRange?: [number, number];
  /** Width */
  width?: number | string;
  /** Height */
  height?: number;
  /** Loading state */
  loading?: boolean;
  /** Enable zoom */
  enableZoom?: boolean;
  /** Custom className */
  className?: string;
}

export const ContactWindowChart = memo(
  forwardRef<AstroChartHandle, ContactWindowChartProps>(
    function ContactWindowChart(
      {
        data = [],
        title = "Contact windows",
        timeRange,
        width = "100%",
        height = 200,
        loading = false,
        enableZoom = true,
        className = "",
        export: exportOptions,
        infoTooltip,
      },
      ref,
    ) {
      // Transform to Gantt-style data
      const { categories, ganttData } = useMemo(() => {
        const cats = data.map((d) => d.name);
        const items: Array<{
          name: string;
          value: [number, number, number, string?];
        }> = [];

        data.forEach((station, stationIdx) => {
          station.windows.forEach((w) => {
            const _color =
              w.signalQuality === "poor"
                ? STATUS_COLORS.critical
                : w.signalQuality === "marginal"
                  ? STATUS_COLORS.caution
                  : STATUS_COLORS.normal;
            items.push({
              name: station.name,
              value: [stationIdx, w.start, w.end, w.label],
            });
          });
        });

        return { categories: cats, ganttData: items };
      }, [data]);

      const series: SeriesData[] = [
        {
          id: "contacts",
          name: "Contacts",
          type: "bar",
          data: [],
        },
      ];

      return (
        <AstroChart
          ref={ref}
          type="bar"
          title={title}
          series={series}
          width={width}
          height={height}
          loading={loading}
          className={className}
          zoom={
            enableZoom
              ? { enabled: true, type: "inside", axis: "x" }
              : undefined
          }
          echartsOptions={{
            tooltip: {
              trigger: "item",
              formatter: ((params: unknown) => {
                const p = params as {
                  value: [number, number, number, string?];
                };
                const [, start, end, label] = p.value;
                const duration = ((end - start) / 60000).toFixed(1);
                return `${label || "Contact"}<br/>Duration: ${duration} min`;
              }) as unknown as string,
            },
            grid: { left: 100, right: 20, top: 40, bottom: 40 },
            xAxis: {
              type: "time",
              min: timeRange?.[0],
              max: timeRange?.[1],
            },
            yAxis: {
              type: "category",
              data: categories,
            },
            series: [
              {
                type: "custom",
                renderItem: ((_params: unknown, api: unknown) => {
                  const a = api as {
                    value: (idx: number) => number;
                    coord: (val: [number, number]) => number[];
                    size: (val: [number, number]) => number[];
                  };
                  const catIdx = a.value(0);
                  const start = a.coord([a.value(1), catIdx]);
                  const end = a.coord([a.value(2), catIdx]);
                  const height = a.size([0, 1])[1] * 0.6;
                  return {
                    type: "rect",
                    shape: {
                      x: start[0],
                      y: start[1] - height / 2,
                      width: end[0] - start[0],
                      height,
                    },
                    style: {
                      fill: STATUS_COLORS.normal,
                      stroke: STATUS_COLORS.standby,
                    },
                  };
                }) as unknown as undefined,
                encode: { x: [1, 2], y: 0 },
                data: ganttData,
              },
            ],
          }}
          export={exportOptions}
          infoTooltip={infoTooltip}
        />
      );
    },
  ),
);

// =============================================================================
// Spectrum / Waterfall Chart – Space TT&C domain
// =============================================================================

export interface SpectrumChartProps extends CommonChartProps {
  /** Spectrum data points */
  data: SpectrumDataPoint[];
  /** Chart title */
  title?: string;
  /** Frequency range [min, max] */
  frequencyRange?: [number, number];
  /** Power range [min, max] dBm */
  powerRange?: [number, number];
  /** Noise floor level */
  noiseFloor?: number;
  /** Width */
  width?: number | string;
  /** Height */
  height?: number;
  /** Loading state */
  loading?: boolean;
  /** Custom className */
  className?: string;
}

export const SpectrumChart = memo(
  forwardRef<AstroChartHandle, SpectrumChartProps>(function SpectrumChart(
    {
      data = [],
      title = "RF spectrum",
      frequencyRange,
      powerRange,
      noiseFloor,
      width = "100%",
      height = 200,
      loading = false,
      className = "",
      export: exportOptions,
      infoTooltip,
    },
    ref,
  ) {
    const series = useMemo(
      (): SeriesData[] => [
        {
          id: "spectrum",
          name: "Power",
          type: "area",
          data: data.map((d) => ({ x: d.frequency, y: d.power })),
          color: ZENDIR_BRAND_COLORS.electric, // Zendir Electric
          smooth: false,
          areaStyle: { opacity: 0.3 },
          symbol: { type: "none" },
        },
      ],
      [data],
    );

    return (
      <AstroChart
        ref={ref}
        type="area"
        title={title}
        series={series}
        width={width}
        height={height}
        loading={loading}
        className={className}
        xAxis={{
          type: "value",
          name: "Frequency",
          min: frequencyRange?.[0],
          max: frequencyRange?.[1],
        }}
        yAxis={{
          type: "value",
          name: "dBm",
          min: powerRange?.[0],
          max: powerRange?.[1],
        }}
        tooltip={{ trigger: "axis", crosshair: "x" }}
        annotations={
          noiseFloor !== undefined
            ? {
                markLines: {
                  data: [
                    {
                      yAxis: noiseFloor,
                      label: `Noise floor (${noiseFloor} dBm)`,
                      color: SOFT_STATUS_COLORS.caution,
                      lineStyle: { type: "dashed" },
                    },
                  ],
                },
              }
            : undefined
        }
        export={exportOptions}
        infoTooltip={infoTooltip}
      />
    );
  }),
);

// =============================================================================
// Maneuver / Delta-V Budget Chart – Propulsion domain
// =============================================================================

export interface ManeuverBudgetChartProps extends CommonChartProps {
  /** Maneuver budget data */
  data: ManeuverBudgetData[];
  /** Chart title */
  title?: string;
  /** Total delta-V budget (for percentage) */
  totalBudget?: number;
  /** Width */
  width?: number | string;
  /** Height */
  height?: number;
  /** Loading state */
  loading?: boolean;
  /** Custom className */
  className?: string;
}

export const ManeuverBudgetChart = memo(
  forwardRef<AstroChartHandle, ManeuverBudgetChartProps>(
    function ManeuverBudgetChart(
      {
        data = [],
        title = "Delta-V budget",
        totalBudget,
        width = "100%",
        height = 200,
        loading = false,
        className = "",
        export: exportOptions,
        infoTooltip,
      },
      ref,
    ) {
      const series = useMemo((): SeriesData[] => {
        // Status colors for executed/planned, Zendir colors for remaining/margin
        const typeColors: Record<string, string> = {
          executed: STATUS_COLORS.normal, // Green (completed)
          planned: STATUS_COLORS.standby, // Cyan (scheduled)
          remaining: ZENDIR_BRAND_COLORS.electric, // Zendir Electric
          margin: ZENDIR_BRAND_COLORS.purple, // Zendir Purple
        };

        return [
          {
            id: "deltaV",
            name: "Delta-V",
            type: "bar",
            data: data.map((d) => ({
              x: d.name,
              y: d.deltaV,
              meta: {
                type: d.type,
                color: typeColors[d.type] || STATUS_COLORS.standby,
              },
            })),
          },
        ];
      }, [data]);

      return (
        <AstroChart
          ref={ref}
          type="bar"
          title={title}
          subtitle={totalBudget ? `Total: ${totalBudget} m/s` : undefined}
          series={series}
          width={width}
          height={height}
          loading={loading}
          className={className}
          xAxis={{ type: "category" }}
          yAxis={{ type: "value", name: "m/s" }}
          tooltip={{ trigger: "axis" }}
          echartsOptions={{
            series: [
              {
                type: "bar",
                data: data.map((d) => ({
                  value: d.deltaV,
                  name: d.name,
                  itemStyle: {
                    color:
                      d.type === "executed"
                        ? STATUS_COLORS.normal
                        : d.type === "planned"
                          ? STATUS_COLORS.standby
                          : d.type === "remaining"
                            ? ZENDIR_BRAND_COLORS.electric
                            : ZENDIR_BRAND_COLORS.purple,
                  },
                })),
              },
            ],
          }}
          export={exportOptions}
          infoTooltip={infoTooltip}
        />
      );
    },
  ),
);

// =============================================================================
// Eclipse Timeline Chart – Orbital mechanics domain
// =============================================================================

export interface EclipseTimelineChartProps extends CommonChartProps {
  /** Eclipse timeline data */
  data: EclipseTimelineData[];
  /** Chart title */
  title?: string;
  /** Time range [start, end] in ms */
  timeRange?: [number, number];
  /** Width */
  width?: number | string;
  /** Height */
  height?: number;
  /** Loading state */
  loading?: boolean;
  /** Enable zoom */
  enableZoom?: boolean;
  /** Custom className */
  className?: string;
}

export const EclipseTimelineChart = memo(
  forwardRef<AstroChartHandle, EclipseTimelineChartProps>(
    function EclipseTimelineChart(
      {
        data = [],
        title = "Eclipse timeline",
        timeRange,
        width = "100%",
        height = 100,
        loading = false,
        enableZoom = true,
        className = "",
        export: exportOptions,
        infoTooltip,
      },
      ref,
    ) {
      const series: SeriesData[] = [
        {
          id: "eclipse",
          name: "Eclipse",
          type: "bar",
          data: [],
        },
      ];

      // Eclipse type colors using Zendir-branded palette
      // Umbra/Penumbra use brand dark tones, sunlight uses status yellow
      const typeColors: Record<string, string> = {
        umbra: ZENDIR_BRAND_COLORS.prussianBlue, // Deep Zendir blue
        penumbra: ZENDIR_BRAND_COLORS.electricDark, // Dark electric
        sunlight: STATUS_COLORS.caution, // Standard yellow for sunlight
      };

      return (
        <AstroChart
          ref={ref}
          type="bar"
          title={title}
          series={series}
          width={width}
          height={height}
          loading={loading}
          className={className}
          zoom={
            enableZoom
              ? { enabled: true, type: "inside", axis: "x" }
              : undefined
          }
          echartsOptions={{
            tooltip: {
              trigger: "item",
              formatter: ((params: unknown) => {
                const p = params as {
                  data: { value: [number, number, string] };
                };
                const [start, end, eclipseType] = p.data.value;
                const duration = ((end - start) / 60000).toFixed(1);
                return `${eclipseType.charAt(0).toUpperCase() + eclipseType.slice(1)}<br/>Duration: ${duration} min`;
              }) as unknown as string,
            },
            grid: { left: 60, right: 20, top: 30, bottom: 30 },
            xAxis: {
              type: "time",
              min: timeRange?.[0],
              max: timeRange?.[1],
            },
            yAxis: {
              type: "category",
              data: [""],
              show: false,
            },
            series: [
              {
                type: "custom",
                renderItem: ((_params: unknown, api: unknown) => {
                  const a = api as {
                    value: (idx: number) => number | string;
                    coord: (val: [number, number]) => number[];
                    size: (val: [number, number]) => number[];
                  };
                  const start = a.coord([a.value(0) as number, 0]);
                  const end = a.coord([a.value(1) as number, 0]);
                  const eclipseType = a.value(2) as string;
                  const h = 30;
                  return {
                    type: "rect",
                    shape: {
                      x: start[0],
                      y: start[1] - h / 2,
                      width: Math.max(end[0] - start[0], 2),
                      height: h,
                    },
                    style: {
                      fill: typeColors[eclipseType] || typeColors.sunlight,
                    },
                  };
                }) as unknown as undefined,
                encode: { x: [0, 1] },
                data: data.map((d) => ({ value: [d.start, d.end, d.type] })),
              },
            ],
          }}
          export={exportOptions}
          infoTooltip={infoTooltip}
        />
      );
    },
  ),
);

// =============================================================================
// Rose Diagram (Polar Histogram for Directional Data)
// =============================================================================

export interface RoseDiagramProps extends CommonChartProps {
  /** Directional data (angle in degrees, magnitude) */
  data: import("./types").RoseDiagramData[];
  /** Chart title */
  title?: string;
  /** Width */
  width?: number | string;
  /** Height */
  height?: number;
  /** Number of bins (sectors) */
  bins?: number;
  /** Loading state */
  loading?: boolean;
  /** Custom className */
  className?: string;
}

export const RoseDiagram = memo(
  forwardRef<AstroChartHandle, RoseDiagramProps>(function RoseDiagram(
    {
      data = [],
      title = "Rose Diagram",
      width = "100%",
      height = 400,
      bins = 16,
      loading = false,
      className = "",
      export: exportOptions,
      infoTooltip,
    },
    ref,
  ) {
    const binData = useMemo(() => {
      // Create bins (e.g., 16 bins = 22.5° each)
      const binSize = 360 / bins;
      const binCounts = new Array(bins).fill(0);

      // Aggregate data into bins
      data.forEach((d) => {
        const normalizedAngle = ((d.direction % 360) + 360) % 360;
        const binIndex = Math.floor(normalizedAngle / binSize);
        binCounts[binIndex] += d.magnitude;
      });

      // Create series data
      return binCounts.map((count, i) => ({
        name: `${(i * binSize).toFixed(0)}°`,
        value: count,
      }));
    }, [data, bins]);

    return (
      <AstroChart
        ref={ref}
        type="bar"
        series={[]}
        title={title}
        width={width}
        height={height}
        loading={loading}
        className={className}
        echartsOptions={{
          polar: {
            radius: ["10%", "70%"],
            center: ["50%", "50%"],
          },
          angleAxis: {
            type: "category",
            data: binData.map((d) => d.name),
            startAngle: 90,
            boundaryGap: true,
            axisLine: { show: false },
            axisTick: { show: false },
            axisLabel: {
              fontSize: 9,
              color: "rgba(255, 255, 255, 0.5)",
              margin: 8,
            },
            splitLine: {
              show: true,
              lineStyle: {
                color: "rgba(100, 116, 139, 0.12)",
                type: "dashed",
              },
            },
          },
          radiusAxis: {
            min: 0,
            axisLine: { show: false },
            axisTick: { show: false },
            axisLabel: { show: false },
            splitLine: {
              show: true,
              lineStyle: {
                color: "rgba(100, 116, 139, 0.15)",
                type: "dashed",
              },
            },
          },
          tooltip: {
            trigger: "item",
            backgroundColor: "rgba(20, 30, 48, 0.95)",
            borderColor: "rgba(100, 116, 139, 0.3)",
            textStyle: { color: "rgba(255, 255, 255, 0.87)" }, // High contrast white
          },
          series: [
            {
              type: "bar",
              data: binData.map((d) => d.value),
              coordinateSystem: "polar",
              roundCap: true,
              itemStyle: {
                // Zendir Electric → Purple gradient
                color: {
                  type: "linear",
                  x: 0,
                  y: 0,
                  x2: 1,
                  y2: 0,
                  colorStops: [
                    { offset: 0, color: ZENDIR_BRAND_COLORS.electric }, // #3E3CFF
                    { offset: 1, color: ZENDIR_BRAND_COLORS.purple }, // #9D70FF
                  ],
                },
                shadowColor: ZENDIR_BRAND_COLORS.purple,
                shadowBlur: 4,
              },
            },
          ],
        }}
        export={exportOptions}
        infoTooltip={infoTooltip}
      />
    );
  }),
);

// =============================================================================
// Heliocentric Orbit Plot (2D/3D with Mode Switching)
// =============================================================================

/** Color map for orbital bodies by type */
const ORBIT_COLOR_MAP: Record<string, string> = {
  // Human-made objects: Zendir Purple/Blue shades
  spacecraft: "#3E3CFF", // Zendir Electric
  satellite: "#9D70FF", // Zendir Purple
  station: "#3548C0", // Zendir Prussian Blue Light
  probe: "#1B2DA0", // Zendir Prussian Blue
  // Natural objects: Realistic colors
  planet: "#5BC0DE", // Teal-blue (default)
  // Debris/Asteroids: Brown/Gray shades
  asteroid: "#8B7355", // Warm brown
  debris: "#6B6B6B", // Gray
  // Comets: Brown with icy tones
  comet: "#5C4033", // Dark brown
};

/** Type labels for tooltips */
const ORBIT_TYPE_LABELS: Record<string, string> = {
  spacecraft: "Spacecraft",
  satellite: "Satellite",
  station: "Space Station",
  probe: "Deep Space Probe",
  planet: "Planet",
  asteroid: "Asteroid",
  debris: "Debris",
  comet: "Comet",
};

export interface HeliocentricOrbitProps extends CommonChartProps {
  /** Orbital bodies/spacecraft data */
  data: import("./types").HeliocentricOrbitData[];
  /** Chart title */
  title?: string;
  /** Width */
  width?: number | string;
  /** Height */
  height?: number;
  /** Show grid */
  showGrid?: boolean;
  /** Enable zoom (scroll to zoom, drag to pan) */
  enableZoom?: boolean;
  /** Show labels on bodies */
  showLabels?: boolean;
  /** Visualization mode: 2D or 3D */
  mode?: "2d" | "3d";
  /** Loading state */
  loading?: boolean;
  /** Custom className */
  className?: string;
  /** Callback when body is clicked */
  onBodyClick?: (body: import("./types").HeliocentricOrbitData) => void;
}

/**
 * HeliocentricOrbitPlot - Interactive 2D/3D orbit visualization
 *
 * Features:
 * - 2D mode: Classic ecliptic plane view with X/Y axes
 * - 3D mode: Full 3D visualization with rotation/zoom (requires echarts-gl)
 * - Interactive hover on both bodies and orbit lines
 * - Zoom/pan controls
 * - Body/orbit highlighting on hover
 */
export const HeliocentricOrbitPlot = memo(
  forwardRef<AstroChartHandle, HeliocentricOrbitProps>(
    function HeliocentricOrbitPlot(
      {
        data = [],
        title = "Heliocentric Orbit",
        width = "100%",
        height = 500,
        showGrid = true,
        enableZoom = true,
        showLabels = true,
        mode = "2d",
        loading = false,
        className = "",
        export: exportOptions,
        infoTooltip,
        onBodyClick: _onBodyClick,
      },
      ref,
    ) {
      const { tokens, mode: themeMode } = useTheme();
      const tooltipStyle = useChartTooltipStyle();
      const _legendStyle = useChartLegendStyle();
      const labelPrimary = tokens.colors.text.primary;
      const labelSecondary = tokens.colors.text.secondary;
      const _labelTertiary = tokens.colors.text.tertiary;
      const labelBg =
        themeMode === "dark"
          ? "rgba(15, 23, 42, 0.72)"
          : "rgba(255, 255, 255, 0.9)";

      // Store body data for tooltip lookup
      const bodyDataMap = useMemo(() => {
        const map = new Map<string, import("./types").HeliocentricOrbitData>();
        data.forEach((body) => map.set(body.name, body));
        return map;
      }, [data]);

      // 2D Options
      const echarts2DOptions = useMemo(() => {
        const seriesArray: any[] = [];

        data.forEach((body, idx) => {
          const bodyColor =
            body.color ||
            ORBIT_COLOR_MAP[body.type || "spacecraft"] ||
            ASTRO_DATA_VIZ_COLORS.mixed[
              idx % ASTRO_DATA_VIZ_COLORS.mixed.length
            ];
          const typeLabel =
            ORBIT_TYPE_LABELS[body.type || "spacecraft"] || "Object";

          // Add orbit path (interactive)
          if (body.orbitPath && body.orbitPath.length > 0) {
            // Calculate orbit stats for tooltip
            const orbitRadius =
              body.semiMajorAxis ||
              Math.max(
                ...body.orbitPath.map((p) => Math.sqrt(p.x * p.x + p.y * p.y)),
              );

            seriesArray.push({
              type: "line",
              name: `${body.name} Orbit`,
              data: body.orbitPath.map((p) => [
                p.x,
                p.y,
                body.name,
                typeLabel,
                orbitRadius,
                body.period,
                body.inclination,
              ]),
              lineStyle: {
                color: bodyColor,
                type: "dashed",
                width: 1.5,
                opacity: 0.6,
              },
              symbol: "none",
              silent: false,
              emphasis: {
                lineStyle: {
                  width: 3.5,
                  opacity: 1,
                  shadowBlur: 8,
                  shadowColor: bodyColor,
                },
              },
              z: 1,
            });
          }

          // Add body position (scatter point)
          seriesArray.push({
            type: "scatter",
            name: body.name,
            symbolSize: body.size || (body.type === "planet" ? 20 : 12),
            data: [
              [
                body.x,
                body.y,
                body.z || 0,
                body.type,
                typeLabel,
                body.description,
                body.semiMajorAxis,
                body.period,
              ],
            ],
            itemStyle: {
              color: bodyColor,
              shadowColor: bodyColor,
              shadowBlur: body.type === "planet" ? 10 : 5,
            },
            label: {
              show: showLabels,
              position: "top",
              formatter: body.name,
              fontSize: 11,
              color: labelSecondary,
              textBorderColor: "transparent",
              textBorderWidth: 0,
            },
            emphasis: {
              scale: 1.6,
              itemStyle: {
                shadowBlur: 20,
              },
              label: {
                show: true,
                fontWeight: "bold",
                fontSize: 12,
                color: labelPrimary,
              },
            },
            z: 10,
          });
        });

        return {
          tooltip: {
            trigger: "item" as const,
            ...tooltipStyle,
            borderWidth: 1,
            padding: [12, 16],
            formatter: ((params: any) => {
              const seriesName = params.seriesName || "";

              // Orbit line tooltip - show orbital parameters
              if (params.seriesType === "line") {
                const [
                  _x,
                  _y,
                  bodyName,
                  typeLabel,
                  orbitRadius,
                  period,
                  inclination,
                ] = params.value || [];
                const bodyInfo = bodyDataMap.get(bodyName);

                let html = `<div style="font-weight:600;font-size:13px;margin-bottom:6px;color:#3E3CFF">${seriesName}</div>`;
                html += `<div style="color:rgba(255,255,255,0.5);font-size:10px;margin-bottom:8px;text-transform:uppercase;letter-spacing:0.5px">${typeLabel || "Orbital Path"}</div>`;
                html += `<div style="border-top:1px solid rgba(100,116,139,0.3);padding-top:8px">`;

                if (orbitRadius) {
                  html += `<div style="display:flex;justify-content:space-between;margin-bottom:4px"><span style="color:rgba(255,255,255,0.6)">Semi-major axis:</span><span style="font-family:monospace">${orbitRadius.toFixed(3)} AU</span></div>`;
                }
                if (period) {
                  html += `<div style="display:flex;justify-content:space-between;margin-bottom:4px"><span style="color:rgba(255,255,255,0.6)">Orbital period:</span><span style="font-family:monospace">${period.toFixed(1)} days</span></div>`;
                }
                if (inclination !== undefined) {
                  html += `<div style="display:flex;justify-content:space-between;margin-bottom:4px"><span style="color:rgba(255,255,255,0.6)">Inclination:</span><span style="font-family:monospace">${inclination.toFixed(1)}°</span></div>`;
                }
                if (bodyInfo?.description) {
                  html += `<div style="margin-top:6px;color:rgba(255,255,255,0.7);font-style:italic;font-size:11px">${bodyInfo.description}</div>`;
                }
                html += `</div>`;
                return html;
              }

              // Scatter point (body) tooltip
              if (params.seriesType === "scatter") {
                const [
                  x,
                  y,
                  z,
                  _bodyType,
                  typeLabel,
                  description,
                  sma,
                  period,
                ] = params.value;
                const distance = Math.sqrt(x * x + y * y + (z || 0) * (z || 0));

                let html = `<div style="font-weight:600;font-size:13px;margin-bottom:4px">${seriesName}</div>`;
                html += `<div style="color:rgba(255,255,255,0.5);font-size:10px;margin-bottom:8px;text-transform:uppercase;letter-spacing:0.5px">${typeLabel || "Object"}</div>`;
                html += `<div style="border-top:1px solid rgba(100,116,139,0.3);padding-top:8px">`;
                html += `<div style="display:flex;justify-content:space-between;margin-bottom:3px"><span style="color:rgba(255,255,255,0.6)">X:</span><span style="font-family:monospace">${x.toFixed(3)} AU</span></div>`;
                html += `<div style="display:flex;justify-content:space-between;margin-bottom:3px"><span style="color:rgba(255,255,255,0.6)">Y:</span><span style="font-family:monospace">${y.toFixed(3)} AU</span></div>`;
                if (z !== 0 && z !== undefined) {
                  html += `<div style="display:flex;justify-content:space-between;margin-bottom:3px"><span style="color:rgba(255,255,255,0.6)">Z:</span><span style="font-family:monospace">${z.toFixed(3)} AU</span></div>`;
                }
                html += `<div style="display:flex;justify-content:space-between;margin-bottom:3px;border-top:1px solid rgba(100,116,139,0.2);padding-top:6px;margin-top:4px"><span style="color:rgba(255,255,255,0.6)">Distance from Sun:</span><span style="font-family:monospace;color:#5BC0DE">${distance.toFixed(3)} AU</span></div>`;
                if (sma) {
                  html += `<div style="display:flex;justify-content:space-between;margin-bottom:3px"><span style="color:rgba(255,255,255,0.6)">Semi-major axis:</span><span style="font-family:monospace">${sma.toFixed(3)} AU</span></div>`;
                }
                if (period) {
                  html += `<div style="display:flex;justify-content:space-between;margin-bottom:3px"><span style="color:rgba(255,255,255,0.6)">Period:</span><span style="font-family:monospace">${period.toFixed(1)} days</span></div>`;
                }
                if (description) {
                  html += `<div style="margin-top:8px;color:rgba(255,255,255,0.7);font-style:italic;font-size:11px;max-width:200px">${description}</div>`;
                }
                html += `</div>`;
                return html;
              }
              return seriesName;
            }) as unknown as string,
          },
          toolbox: {
            show: enableZoom,
            right: 20,
            top: 10,
            feature: {
              dataZoom: { show: true, title: { zoom: "Zoom", back: "Reset" } },
              restore: { show: true, title: "Reset" },
            },
            iconStyle: { borderColor: "rgba(255, 255, 255, 0.5)" },
          },
          dataZoom: enableZoom
            ? [
                {
                  type: "inside" as const,
                  xAxisIndex: 0,
                  filterMode: "none" as const,
                },
                {
                  type: "inside" as const,
                  yAxisIndex: 0,
                  filterMode: "none" as const,
                },
              ]
            : undefined,
          grid: {
            containLabel: true,
            left: 60,
            right: enableZoom ? 80 : 40,
            top: 60,
            bottom: 60,
          },
          xAxis: {
            type: "value" as const,
            name: "X (AU)",
            nameLocation: "middle" as const,
            nameGap: 30,
            nameTextStyle: { color: "rgba(255, 255, 255, 0.5)" },
            splitLine: {
              show: showGrid,
              lineStyle: { color: "rgba(100, 116, 139, 0.15)" },
            },
            axisLine: {
              show: true,
              lineStyle: { color: "rgba(100, 116, 139, 0.3)" },
            },
            axisLabel: { color: "rgba(255, 255, 255, 0.5)" },
          },
          yAxis: {
            type: "value" as const,
            name: "Y (AU)",
            nameLocation: "middle" as const,
            nameGap: 40,
            nameTextStyle: { color: "rgba(255, 255, 255, 0.5)" },
            splitLine: {
              show: showGrid,
              lineStyle: { color: "rgba(100, 116, 139, 0.15)" },
            },
            axisLine: {
              show: true,
              lineStyle: { color: "rgba(100, 116, 139, 0.3)" },
            },
            axisLabel: { color: "rgba(255, 255, 255, 0.5)" },
          },
          animation: true,
          animationDuration: 300,
          series: seriesArray,
        };
      }, [data, showGrid, enableZoom, showLabels, bodyDataMap, tooltipStyle]);

      // 3D Options (requires echarts-gl)
      const echarts3DOptions = useMemo(() => {
        const seriesArray: any[] = [];

        data.forEach((body, idx) => {
          const bodyColor =
            body.color ||
            ORBIT_COLOR_MAP[body.type || "spacecraft"] ||
            ASTRO_DATA_VIZ_COLORS.mixed[
              idx % ASTRO_DATA_VIZ_COLORS.mixed.length
            ];
          const typeLabel =
            ORBIT_TYPE_LABELS[body.type || "spacecraft"] || "Object";

          // Orbit path as line3D series
          if (body.orbitPath && body.orbitPath.length > 0) {
            const orbitData = body.orbitPath.map((p) => [p.x, p.y, p.z || 0]);
            // Close the orbit loop
            if (orbitData.length > 2) {
              orbitData.push(orbitData[0]);
            }
            seriesArray.push({
              type: "line3D",
              name: `${body.name} Orbit`,
              coordinateSystem: "cartesian3D",
              data: orbitData,
              lineStyle: {
                color: bodyColor,
                width: 1.5,
                opacity: 0.6,
              },
              silent: false,
            });
          }

          // Body position as scatter3D series
          seriesArray.push({
            type: "scatter3D",
            name: body.name,
            coordinateSystem: "cartesian3D",
            data: [
              {
                name: body.name,
                value: [body.x, body.y, body.z || 0],
                itemStyle: { color: bodyColor },
                typeLabel,
                description: body.description,
              },
            ],
            symbolSize: body.size || (body.type === "planet" ? 16 : 10),
            itemStyle: { color: bodyColor },
            label: {
              show: showLabels,
              formatter: body.name,
              textStyle: {
                color: labelPrimary,
                fontSize: 11,
                backgroundColor: labelBg,
                padding: [2, 4],
                borderRadius: 2,
              },
            },
            emphasis: {
              itemStyle: { opacity: 1 },
              label: { show: true },
            },
          });
        });

        return {
          tooltip: {
            show: true,
            ...tooltipStyle,
            formatter: ((params: any) => {
              const d = params.data || {};
              const seriesName = params.seriesName || "";

              // Line series (orbit)
              if (params.seriesType === "line3D") {
                return `<div style="font-weight:600">${seriesName}</div>`;
              }

              // Scatter (body)
              const [x, y, z] = d.value || params.value || [0, 0, 0];
              const distance = Math.sqrt(x * x + y * y + z * z);

              let html = `<div style="font-weight:600;font-size:13px;margin-bottom:4px">${d.name || seriesName}</div>`;
              html += `<div style="color:rgba(255,255,255,0.5);font-size:10px;margin-bottom:8px;text-transform:uppercase">${d.typeLabel || "Object"}</div>`;
              html += `<div style="border-top:1px solid rgba(100,116,139,0.3);padding-top:8px">`;
              html += `<div>X: <span style="font-family:monospace">${x.toFixed(3)}</span> AU</div>`;
              html += `<div>Y: <span style="font-family:monospace">${y.toFixed(3)}</span> AU</div>`;
              html += `<div>Z: <span style="font-family:monospace">${z.toFixed(3)}</span> AU</div>`;
              html += `<div style="margin-top:4px;color:#5BC0DE">Distance: <span style="font-family:monospace">${distance.toFixed(3)}</span> AU</div>`;
              if (d.description) {
                html += `<div style="margin-top:6px;color:rgba(255,255,255,0.7);font-style:italic;font-size:11px">${d.description}</div>`;
              }
              html += `</div>`;
              return html;
            }) as unknown as string,
          },
          grid3D: {
            boxWidth: 200,
            boxHeight: 60,
            boxDepth: 200,
            viewControl: {
              autoRotate: false,
              autoRotateAfterStill: 5,
              autoRotateSpeed: 5,
              distance: 280,
              alpha: 20,
              beta: 40,
              rotateSensitivity: 2,
              zoomSensitivity: 1.5,
              panSensitivity: 1,
            },
            light: {
              main: { intensity: 1.2, shadow: false },
              ambient: { intensity: 0.4 },
            },
            postEffect: {
              enable: true,
              bloom: { enable: true, intensity: 0.05 },
            },
          },
          xAxis3D: {
            type: "value" as const,
            name: "X (AU)",
            nameTextStyle: { color: "rgba(255, 255, 255, 0.5)" },
            axisLine: { lineStyle: { color: "rgba(100, 116, 139, 0.4)" } },
            axisLabel: {
              color: "rgba(255, 255, 255, 0.4)",
              formatter: (v: number) => v.toFixed(1),
            },
            splitLine: {
              show: showGrid,
              lineStyle: { color: "rgba(100, 116, 139, 0.1)" },
            },
          },
          yAxis3D: {
            type: "value" as const,
            name: "Y (AU)",
            nameTextStyle: { color: "rgba(255, 255, 255, 0.5)" },
            axisLine: { lineStyle: { color: "rgba(100, 116, 139, 0.4)" } },
            axisLabel: {
              color: "rgba(255, 255, 255, 0.4)",
              formatter: (v: number) => v.toFixed(1),
            },
            splitLine: {
              show: showGrid,
              lineStyle: { color: "rgba(100, 116, 139, 0.1)" },
            },
          },
          zAxis3D: {
            type: "value" as const,
            name: "Z (AU)",
            nameTextStyle: { color: "rgba(255, 255, 255, 0.5)" },
            axisLine: { lineStyle: { color: "rgba(100, 116, 139, 0.4)" } },
            axisLabel: {
              color: "rgba(255, 255, 255, 0.4)",
              formatter: (v: number) => v.toFixed(1),
            },
            splitLine: {
              show: showGrid,
              lineStyle: { color: "rgba(100, 116, 139, 0.1)" },
            },
          },
          series: seriesArray,
        };
      }, [data, showGrid, showLabels]);

      // Cast to any for 3D options since echarts-gl types extend base ECharts
      const echartsOptions =
        mode === "3d" ? (echarts3DOptions as any) : echarts2DOptions;

      return (
        <AstroChart
          ref={ref}
          type="scatter"
          series={[]}
          title={title}
          width={width}
          height={height}
          loading={loading}
          className={className}
          echartsOptions={echartsOptions}
          export={exportOptions}
          infoTooltip={infoTooltip}
        />
      );
    },
  ),
);

// =============================================================================
// Scatter3D Chart - Constellation Visualization, Debris Fields
// =============================================================================

export interface Scatter3DChartProps extends CommonChartProps {
  /** 3D scatter data series */
  data: import("./types").Scatter3DData[];
  /** Chart title */
  title?: string;
  /** Chart width */
  width?: number | string;
  /** Chart height */
  height?: number;
  /** Show grid/axis */
  showGrid?: boolean;
  /** X-axis label */
  xAxisLabel?: string;
  /** Y-axis label */
  yAxisLabel?: string;
  /** Z-axis label */
  zAxisLabel?: string;
  /** Auto rotate */
  autoRotate?: boolean;
  /** Loading state */
  loading?: boolean;
  /** Custom className */
  className?: string;
}

/**
 * Scatter3DChart - 3D scatter plot for constellation analysis, debris fields
 *
 * Perfect for visualizing:
 * - Satellite constellation distributions
 * - Space debris fields
 * - Multi-dimensional parameter spaces
 * - Position clustering analysis
 */
export const Scatter3DChart = memo(
  forwardRef<AstroChartHandle, Scatter3DChartProps>(function Scatter3DChart(
    {
      data = [],
      title,
      width = "100%",
      height = 500,
      showGrid = true,
      xAxisLabel = "X",
      yAxisLabel = "Y",
      zAxisLabel = "Z",
      autoRotate = false,
      loading = false,
      className = "",
      export: exportOptions,
      infoTooltip,
    },
    ref,
  ) {
    const glReady = useEchartsGlReady();
    const tooltipStyle = useChartTooltipStyle();
    const legendStyle = useChartLegendStyle();

    const echartsOptions = useMemo(() => {
      const seriesArray = data.map((series, idx) => ({
        type: "scatter3D" as const,
        name: series.name,
        data: series.data.map((p) => ({
          name: p.label || series.name,
          value: [p.x, p.y, p.z],
          itemStyle: p.color ? { color: p.color } : undefined,
          meta: p.meta,
        })),
        symbolSize:
          typeof series.symbolSize === "number" ? series.symbolSize : 12,
        itemStyle: {
          color:
            series.color ||
            ASTRO_DATA_VIZ_COLORS.mixed[
              idx % ASTRO_DATA_VIZ_COLORS.mixed.length
            ],
        },
        label: {
          show: false,
        },
        emphasis: {
          itemStyle: { opacity: 1 },
          label: {
            show: true,
            formatter: (params: any) => params.data?.name || "",
          },
        },
      }));

      return {
        tooltip: {
          show: true,
          ...tooltipStyle,
          formatter: ((params: any) => {
            const d = params.data || {};
            const [x, y, z] = d.value || [0, 0, 0];
            let html = `<div style="font-weight:600;font-size:13px;margin-bottom:4px">${d.name || params.seriesName}</div>`;
            html += `<div>${xAxisLabel}: <span style="font-family:monospace">${x.toFixed(2)}</span></div>`;
            html += `<div>${yAxisLabel}: <span style="font-family:monospace">${y.toFixed(2)}</span></div>`;
            html += `<div>${zAxisLabel}: <span style="font-family:monospace">${z.toFixed(2)}</span></div>`;
            return html;
          }) as unknown as string,
        },
        legend: {
          show: data.length > 1,
          top: 10,
          ...legendStyle,
        },
        grid3D: {
          boxWidth: 100,
          boxHeight: 100,
          boxDepth: 100,
          viewControl: {
            autoRotate,
            autoRotateSpeed: 5,
            distance: 200,
            alpha: 25,
            beta: 45,
            rotateSensitivity: 2,
            zoomSensitivity: 1.5,
          },
          light: {
            main: { intensity: 1.2, shadow: false },
            ambient: { intensity: 0.4 },
          },
          postEffect: {
            enable: true,
            bloom: { enable: true, intensity: 0.05 },
          },
        },
        xAxis3D: {
          type: "value" as const,
          name: xAxisLabel,
          nameTextStyle: { color: "rgba(255, 255, 255, 0.5)" },
          axisLine: { lineStyle: { color: "rgba(100, 116, 139, 0.4)" } },
          axisLabel: { color: "rgba(255, 255, 255, 0.4)" },
          splitLine: {
            show: showGrid,
            lineStyle: { color: "rgba(100, 116, 139, 0.1)" },
          },
        },
        yAxis3D: {
          type: "value" as const,
          name: yAxisLabel,
          nameTextStyle: { color: "rgba(255, 255, 255, 0.5)" },
          axisLine: { lineStyle: { color: "rgba(100, 116, 139, 0.4)" } },
          axisLabel: { color: "rgba(255, 255, 255, 0.4)" },
          splitLine: {
            show: showGrid,
            lineStyle: { color: "rgba(100, 116, 139, 0.1)" },
          },
        },
        zAxis3D: {
          type: "value" as const,
          name: zAxisLabel,
          nameTextStyle: { color: "rgba(255, 255, 255, 0.5)" },
          axisLine: { lineStyle: { color: "rgba(100, 116, 139, 0.4)" } },
          axisLabel: { color: "rgba(255, 255, 255, 0.4)" },
          splitLine: {
            show: showGrid,
            lineStyle: { color: "rgba(100, 116, 139, 0.1)" },
          },
        },
        series: seriesArray,
      };
    }, [
      data,
      showGrid,
      xAxisLabel,
      yAxisLabel,
      zAxisLabel,
      autoRotate,
      tooltipStyle,
      legendStyle,
    ]);

    if (!glReady)
      return (
        <AstroChart
          ref={ref}
          type="scatter"
          series={[]}
          title={title}
          width={width}
          height={height}
          loading
          className={className}
        />
      );
    return (
      <AstroChart
        ref={ref}
        type="scatter"
        series={[]}
        title={title}
        width={width}
        height={height}
        loading={loading}
        className={className}
        echartsOptions={echartsOptions as any}
        export={exportOptions}
        infoTooltip={infoTooltip}
      />
    );
  }),
);

// =============================================================================
// Bar3D Chart - Telemetry Comparisons, Multi-Satellite Analysis
// =============================================================================

export interface Bar3DChartProps extends CommonChartProps {
  /** 3D bar data */
  data: import("./types").Bar3DData[];
  /** Chart title */
  title?: string;
  /** Chart width */
  width?: number | string;
  /** Chart height */
  height?: number;
  /** X-axis categories (or auto-derived) */
  xCategories?: string[];
  /** Y-axis categories (or auto-derived) */
  yCategories?: string[];
  /** X-axis label */
  xAxisLabel?: string;
  /** Y-axis label */
  yAxisLabel?: string;
  /** Z-axis label */
  zAxisLabel?: string;
  /** Bar shading style */
  shading?: "color" | "lambert" | "realistic";
  /** Auto rotate */
  autoRotate?: boolean;
  /** Loading state */
  loading?: boolean;
  /** Custom className */
  className?: string;
}

/**
 * Bar3DChart - 3D bar chart for multi-dimensional comparisons
 *
 * Perfect for visualizing:
 * - Telemetry comparisons across satellites
 * - Parameter variations over time
 * - Multi-subsystem status comparisons
 * - Resource allocation matrices
 */
export const Bar3DChart = memo(
  forwardRef<AstroChartHandle, Bar3DChartProps>(function Bar3DChart(
    {
      data = [],
      title,
      width = "100%",
      height = 500,
      xCategories = [],
      yCategories = [],
      xAxisLabel = "X",
      yAxisLabel = "Y",
      zAxisLabel = "Value",
      shading = "lambert",
      autoRotate = false,
      loading = false,
      className = "",
      export: exportOptions,
      infoTooltip,
    },
    ref,
  ) {
    const glReady = useEchartsGlReady();
    const tooltipStyle = useChartTooltipStyle();
    const visualMapStyle = useChartVisualMapStyle();
    const { tokens } = useTheme();
    const labelPrimary = tokens.colors.text.primary;
    const labelSecondary = tokens.colors.text.secondary;
    const labelTertiary = tokens.colors.text.tertiary;

    // Derive categories if not provided
    const derivedXCategories = xCategories || [
      ...new Set(data.map((d) => String(d.x))),
    ];
    const derivedYCategories = yCategories || [
      ...new Set(data.map((d) => String(d.y))),
    ];

    const echartsOptions = useMemo(() => {
      const seriesData = data.map((d) => {
        const xIdx = derivedXCategories.indexOf(String(d.x));
        const yIdx = derivedYCategories.indexOf(String(d.y));
        return {
          value: [xIdx, yIdx, d.z],
          name: d.label || `${d.x} - ${d.y}`,
          itemStyle: d.color ? { color: d.color } : undefined,
        };
      });

      // Calculate max Z for scaling
      const maxZ = Math.max(...data.map((d) => d.z), 1);

      return {
        tooltip: {
          show: true,
          ...tooltipStyle,
          formatter: ((params: any) => {
            const d = params.data || {};
            const [xIdx, yIdx, z] = d.value || [0, 0, 0];
            const xLabel = derivedXCategories[xIdx] || xIdx;
            const yLabel = derivedYCategories[yIdx] || yIdx;
            let html = `<div style="font-weight:600;font-size:13px;margin-bottom:4px">${d.name || ""}</div>`;
            html += `<div>${xAxisLabel}: ${xLabel}</div>`;
            html += `<div>${yAxisLabel}: ${yLabel}</div>`;
            html += `<div style="color:#3E3CFF">${zAxisLabel}: <span style="font-family:monospace">${z.toFixed(2)}</span></div>`;
            return html;
          }) as unknown as string,
        },
        visualMap: {
          show: true,
          min: 0,
          max: maxZ,
          inRange: {
            color: ["#3E3CFF", "#9D70FF", "#22d3ee", "#56f000"],
          },
          ...visualMapStyle,
          right: 10,
          top: "center",
        },
        grid3D: {
          boxWidth: 100,
          boxHeight: 80,
          boxDepth: 100,
          viewControl: {
            autoRotate,
            autoRotateSpeed: 3,
            distance: 250,
            alpha: 25,
            beta: 45,
            rotateSensitivity: 2,
            zoomSensitivity: 1.5,
          },
          light: {
            main: { intensity: 1.1, shadow: true, shadowQuality: "medium" },
            ambient: { intensity: 0.4 },
          },
        },
        xAxis3D: {
          type: "category" as const,
          name: xAxisLabel,
          data: derivedXCategories,
          nameTextStyle: { color: labelSecondary },
          axisLine: { lineStyle: { color: "rgba(100, 116, 139, 0.4)" } },
          axisLabel: { color: labelSecondary, fontSize: 10 },
        },
        yAxis3D: {
          type: "category" as const,
          name: yAxisLabel,
          data: derivedYCategories,
          nameTextStyle: { color: labelSecondary },
          axisLine: { lineStyle: { color: "rgba(100, 116, 139, 0.4)" } },
          axisLabel: { color: labelSecondary, fontSize: 10 },
        },
        zAxis3D: {
          type: "value" as const,
          name: zAxisLabel,
          nameTextStyle: { color: labelSecondary },
          axisLine: { lineStyle: { color: "rgba(100, 116, 139, 0.4)" } },
          axisLabel: { color: labelTertiary },
        },
        series: [
          {
            type: "bar3D" as const,
            data: seriesData,
            shading,
            barSize: 15,
            bevelSize: 0.1,
            label: {
              show: false,
            },
            emphasis: {
              label: {
                show: true,
                formatter: (params: any) => params.data?.name || "",
                textStyle: { color: labelPrimary, fontSize: 11 },
              },
            },
          },
        ],
      };
    }, [
      data,
      derivedXCategories,
      derivedYCategories,
      xAxisLabel,
      yAxisLabel,
      zAxisLabel,
      shading,
      autoRotate,
      tooltipStyle,
      visualMapStyle,
      labelPrimary,
      labelSecondary,
      labelTertiary,
    ]);

    if (!glReady)
      return (
        <AstroChart
          ref={ref}
          type="bar"
          series={[]}
          title={title}
          width={width}
          height={height}
          loading
          className={className}
        />
      );
    return (
      <AstroChart
        ref={ref}
        type="bar"
        series={[]}
        title={title}
        width={width}
        height={height}
        loading={loading}
        className={className}
        echartsOptions={echartsOptions as any}
        export={exportOptions}
        infoTooltip={infoTooltip}
      />
    );
  }),
);

// =============================================================================
// Surface3D Chart - Signal Strength Maps, Thermal Analysis
// =============================================================================

export interface Surface3DChartProps extends CommonChartProps {
  /** Surface data (2D array or structured) */
  data: import("./types").Surface3DData;
  /** Chart title */
  title?: string;
  /** Chart width */
  width?: number | string;
  /** Chart height */
  height?: number;
  /** X-axis label */
  xAxisLabel?: string;
  /** Y-axis label */
  yAxisLabel?: string;
  /** Z-axis label */
  zAxisLabel?: string;
  /** Surface shading style */
  shading?: "color" | "lambert" | "realistic";
  /** Auto rotate */
  autoRotate?: boolean;
  /** Loading state */
  loading?: boolean;
  /** Custom className */
  className?: string;
}

/**
 * Surface3DChart - 3D surface visualization for continuous data
 *
 * Perfect for visualizing:
 * - Signal strength heatmaps over coverage areas
 * - Thermal distribution surfaces
 * - Terrain/topography models
 * - Radiation patterns
 */
export const Surface3DChart = memo(
  forwardRef<AstroChartHandle, Surface3DChartProps>(function Surface3DChart(
    {
      data = { data: [] },
      title,
      width = "100%",
      height = 500,
      xAxisLabel = "X",
      yAxisLabel = "Y",
      zAxisLabel = "Z",
      shading = "realistic",
      autoRotate = false,
      loading = false,
      className = "",
      export: exportOptions,
      infoTooltip,
    },
    ref,
  ) {
    const glReady = useEchartsGlReady();
    const tooltipStyle = useChartTooltipStyle();
    const visualMapStyle = useChartVisualMapStyle();

    const echartsOptions = useMemo(() => {
      // Convert 2D array to surface data format
      const surfaceData: number[][] = [];
      const rows = data.data.length;
      const cols = rows > 0 ? data.data[0].length : 0;

      for (let i = 0; i < rows; i++) {
        for (let j = 0; j < cols; j++) {
          const x = data.xRange
            ? data.xRange[0] +
              (data.xRange[1] - data.xRange[0]) * (i / (rows - 1 || 1))
            : i;
          const y = data.yRange
            ? data.yRange[0] +
              (data.yRange[1] - data.yRange[0]) * (j / (cols - 1 || 1))
            : j;
          surfaceData.push([x, y, data.data[i][j]]);
        }
      }

      // Calculate min/max for visual map
      const allZ = data.data.flat();
      const minZ = Math.min(...allZ);
      const maxZ = Math.max(...allZ);

      // Color map based on selection
      const colorRange =
        data.colorMap === "thermal"
          ? ["#0d0887", "#6a00a8", "#b12a90", "#e16462", "#fca636", "#f0f921"]
          : data.colorMap === "viridis"
            ? ["#440154", "#414487", "#2a788e", "#22a884", "#7ad151", "#fde725"]
            : data.colorStops?.map((s) => s.color) || [
                "#1B2DA0",
                "#3E3CFF",
                "#9D70FF",
                "#22d3ee",
                "#56f000",
              ];

      return {
        tooltip: {
          show: true,
          ...tooltipStyle,
        },
        visualMap: {
          show: true,
          min: minZ,
          max: maxZ,
          inRange: { color: colorRange },
          ...visualMapStyle,
          right: 10,
          top: "center",
        },
        grid3D: {
          boxWidth: 100,
          boxHeight: 60,
          boxDepth: 100,
          viewControl: {
            autoRotate,
            autoRotateSpeed: 3,
            distance: 220,
            alpha: 30,
            beta: 45,
            rotateSensitivity: 2,
            zoomSensitivity: 1.5,
          },
          light: {
            main: { intensity: 1.2, shadow: true, shadowQuality: "medium" },
            ambient: { intensity: 0.5 },
          },
          postEffect: {
            enable: true,
            SSAO: { enable: true, radius: 2 },
          },
        },
        xAxis3D: {
          type: "value" as const,
          name: xAxisLabel,
          nameTextStyle: { color: "rgba(255, 255, 255, 0.5)" },
          axisLine: { lineStyle: { color: "rgba(100, 116, 139, 0.4)" } },
          axisLabel: { color: "rgba(255, 255, 255, 0.4)" },
        },
        yAxis3D: {
          type: "value" as const,
          name: yAxisLabel,
          nameTextStyle: { color: "rgba(255, 255, 255, 0.5)" },
          axisLine: { lineStyle: { color: "rgba(100, 116, 139, 0.4)" } },
          axisLabel: { color: "rgba(255, 255, 255, 0.4)" },
        },
        zAxis3D: {
          type: "value" as const,
          name: zAxisLabel,
          nameTextStyle: { color: "rgba(255, 255, 255, 0.5)" },
          axisLine: { lineStyle: { color: "rgba(100, 116, 139, 0.4)" } },
          axisLabel: { color: "rgba(255, 255, 255, 0.4)" },
        },
        series: [
          {
            type: "surface" as const,
            data: surfaceData,
            shading,
            wireframe: data.wireframe
              ? {
                  show: true,
                  lineStyle: { color: "rgba(255, 255, 255, 0.2)", width: 0.5 },
                }
              : { show: false },
          },
        ],
      };
    }, [
      data,
      xAxisLabel,
      yAxisLabel,
      zAxisLabel,
      shading,
      autoRotate,
      tooltipStyle,
      visualMapStyle,
    ]);

    if (!glReady)
      return (
        <AstroChart
          ref={ref}
          type="scatter"
          series={[]}
          title={title}
          width={width}
          height={height}
          loading
          className={className}
        />
      );
    return (
      <AstroChart
        ref={ref}
        type="scatter"
        series={[]}
        title={title}
        width={width}
        height={height}
        loading={loading}
        className={className}
        echartsOptions={echartsOptions as any}
        export={exportOptions}
        infoTooltip={infoTooltip}
      />
    );
  }),
);

// =============================================================================
// Lines3D Chart - Debris Trajectories, Satellite Links
// =============================================================================

export interface Lines3DChartProps extends CommonChartProps {
  /** 3D line paths */
  data: import("./types").Lines3DData[];
  /** Chart title */
  title?: string;
  /** Chart width */
  width?: number | string;
  /** Chart height */
  height?: number;
  /** Show grid */
  showGrid?: boolean;
  /** X-axis label */
  xAxisLabel?: string;
  /** Y-axis label */
  yAxisLabel?: string;
  /** Z-axis label */
  zAxisLabel?: string;
  /** Show flow animation effect */
  showFlowEffect?: boolean;
  /** Auto rotate */
  autoRotate?: boolean;
  /** Loading state */
  loading?: boolean;
  /** Custom className */
  className?: string;
}

/**
 * Lines3DChart - 3D line paths for trajectories and connections
 *
 * Perfect for visualizing:
 * - Space debris trajectories
 * - Inter-satellite link networks
 * - Transfer orbit paths
 * - Multi-body trajectory visualization
 */
export const Lines3DChart = memo(
  forwardRef<AstroChartHandle, Lines3DChartProps>(function Lines3DChart(
    {
      data = [],
      title,
      width = "100%",
      height = 500,
      showGrid = true,
      xAxisLabel = "X",
      yAxisLabel = "Y",
      zAxisLabel = "Z",
      showFlowEffect = false,
      autoRotate = false,
      loading = false,
      className = "",
      export: exportOptions,
      infoTooltip,
    },
    ref,
  ) {
    const { tokens, mode } = useTheme();
    const tooltipStyle = useChartTooltipStyle();
    const legendStyle = useChartLegendStyle();
    const labelPrimary = tokens.colors.text.primary;
    const labelSecondary = tokens.colors.text.secondary;
    const _labelTertiary = tokens.colors.text.tertiary;
    const _labelBg =
      mode === "dark" ? "rgba(15, 23, 42, 0.72)" : "rgba(255, 255, 255, 0.9)";

    const echartsOptions = useMemo(() => {
      const seriesArray: any[] = [];

      data.forEach((line, idx) => {
        const lineColor =
          line.color ||
          ASTRO_DATA_VIZ_COLORS.mixed[idx % ASTRO_DATA_VIZ_COLORS.mixed.length];

        // Main line
        seriesArray.push({
          type: "line3D" as const,
          name: line.name,
          coordinateSystem: "cartesian3D",
          data: line.path.map((p) => [p.x, p.y, p.z]),
          lineStyle: {
            color: lineColor,
            width: line.lineWidth || 2,
            type: line.lineStyle || "solid",
            opacity: 0.8,
          },
        });

        // Endpoints as scatter
        if (line.showEndpoints) {
          seriesArray.push({
            type: "scatter3D" as const,
            name: `${line.name} Points`,
            coordinateSystem: "cartesian3D",
            data: [
              {
                value: [line.path[0].x, line.path[0].y, line.path[0].z],
                name: "Start",
              },
              {
                value: [
                  line.path[line.path.length - 1].x,
                  line.path[line.path.length - 1].y,
                  line.path[line.path.length - 1].z,
                ],
                name: "End",
              },
            ],
            symbolSize: 8,
            itemStyle: { color: lineColor },
          });
        }
      });

      return {
        tooltip: {
          show: true,
          ...tooltipStyle,
          formatter: ((params: any) => {
            const d = params.data || {};
            if (params.seriesType === "line3D") {
              return `<div style="font-weight:600">${params.seriesName}</div>`;
            }
            const [x, y, z] = d.value || [0, 0, 0];
            return `<div style="font-weight:600">${d.name || params.seriesName}</div>
              <div>${xAxisLabel}: ${x.toFixed(2)}</div>
              <div>${yAxisLabel}: ${y.toFixed(2)}</div>
              <div>${zAxisLabel}: ${z.toFixed(2)}</div>`;
          }) as unknown as string,
        },
        legend: {
          show: data.length > 1,
          top: 10,
          ...legendStyle,
        },
        grid3D: {
          boxWidth: 100,
          boxHeight: 100,
          boxDepth: 100,
          viewControl: {
            autoRotate,
            autoRotateSpeed: 3,
            distance: 200,
            alpha: 25,
            beta: 45,
            rotateSensitivity: 2,
            zoomSensitivity: 1.5,
          },
          light: {
            main: { intensity: 1.2, shadow: false },
            ambient: { intensity: 0.4 },
          },
          postEffect: {
            enable: true,
            bloom: { enable: showFlowEffect, intensity: 0.1 },
          },
        },
        xAxis3D: {
          type: "value" as const,
          name: xAxisLabel,
          nameTextStyle: { color: "rgba(255, 255, 255, 0.5)" },
          axisLine: { lineStyle: { color: "rgba(100, 116, 139, 0.4)" } },
          axisLabel: { color: "rgba(255, 255, 255, 0.4)" },
          splitLine: {
            show: showGrid,
            lineStyle: { color: "rgba(100, 116, 139, 0.1)" },
          },
        },
        yAxis3D: {
          type: "value" as const,
          name: yAxisLabel,
          nameTextStyle: { color: "rgba(255, 255, 255, 0.5)" },
          axisLine: { lineStyle: { color: "rgba(100, 116, 139, 0.4)" } },
          axisLabel: { color: "rgba(255, 255, 255, 0.4)" },
          splitLine: {
            show: showGrid,
            lineStyle: { color: "rgba(100, 116, 139, 0.1)" },
          },
        },
        zAxis3D: {
          type: "value" as const,
          name: zAxisLabel,
          nameTextStyle: { color: "rgba(255, 255, 255, 0.5)" },
          axisLine: { lineStyle: { color: "rgba(100, 116, 139, 0.4)" } },
          axisLabel: { color: "rgba(255, 255, 255, 0.4)" },
          splitLine: {
            show: showGrid,
            lineStyle: { color: "rgba(100, 116, 139, 0.1)" },
          },
        },
        series: seriesArray,
      };
    }, [
      data,
      showGrid,
      xAxisLabel,
      yAxisLabel,
      zAxisLabel,
      showFlowEffect,
      autoRotate,
      tooltipStyle,
      legendStyle,
    ]);

    return (
      <AstroChart
        ref={ref}
        type="scatter"
        series={[]}
        title={title}
        width={width}
        height={height}
        loading={loading}
        className={className}
        echartsOptions={echartsOptions as any}
        export={exportOptions}
        infoTooltip={infoTooltip}
      />
    );
  }),
);

// =============================================================================
// SPACE-SPECIFIC 3D CHARTS
// =============================================================================

// =============================================================================
// SphericalRadar3DChart - Object Tracking in Spherical Coordinates
// =============================================================================

export interface SphericalRadar3DData {
  /** Object identifier */
  id: string;
  /** Object name */
  name: string;
  /** Azimuth angle (degrees, 0-360) */
  azimuth: number;
  /** Elevation angle (degrees, -90 to 90) */
  elevation: number;
  /** Range/distance */
  range: number;
  /** Object category */
  category?: "satellite" | "debris" | "unknown" | "friendly" | "hostile";
  /** Color override */
  color?: string;
  /** Size override */
  size?: number;
}

export interface SphericalRadar3DChartProps extends CommonChartProps {
  /** Tracked objects */
  data: SphericalRadar3DData[];
  /** Chart title */
  title?: string;
  /** Chart width */
  width?: number | string;
  /** Chart height */
  height?: number;
  /** Maximum range for scaling */
  maxRange?: number;
  /** Show range rings */
  showRangeRings?: boolean;
  /** Auto rotate */
  autoRotate?: boolean;
  /** Loading state */
  loading?: boolean;
  /** Custom className */
  className?: string;
}

/**
 * SphericalRadar3DChart - 3D radar-style object tracking visualization
 *
 * USE CASES:
 * - Ground station tracking view (azimuth/elevation/range)
 * - Space surveillance radar displays
 * - Proximity operations around spacecraft
 * - Air/space traffic control visualization
 * - Collision avoidance monitoring
 */
export const SphericalRadar3DChart = memo(
  forwardRef<AstroChartHandle, SphericalRadar3DChartProps>(
    function SphericalRadar3DChart(
      {
        data = [],
        title,
        width = "100%",
        height = 500,
        maxRange = 1000,
        showRangeRings = true,
        autoRotate = false,
        loading = false,
        className = "",
        export: exportOptions,
        infoTooltip,
      },
      ref,
    ) {
      const glReady = useEchartsGlReady();
      const tooltipStyle = useChartTooltipStyle();
      const _legendStyle = useChartLegendStyle();

      const categoryColors: Record<string, string> = {
        satellite: "#56f000",
        debris: "#ff3838",
        unknown: "#fce83a",
        friendly: "#3E3CFF",
        hostile: "#ff3838",
      };

      const echartsOptions = useMemo(() => {
        // Convert spherical to Cartesian for 3D display
        const scatterData = data.map((obj) => {
          const azRad = (obj.azimuth * Math.PI) / 180;
          const elRad = (obj.elevation * Math.PI) / 180;
          const r = (obj.range / maxRange) * 100;

          return {
            name: obj.name,
            value: [
              r * Math.cos(elRad) * Math.sin(azRad),
              r * Math.sin(elRad),
              r * Math.cos(elRad) * Math.cos(azRad),
            ],
            itemStyle: {
              color: obj.color || categoryColors[obj.category || "unknown"],
            },
            symbolSize: obj.size || 12,
            category: obj.category,
            azimuth: obj.azimuth,
            elevation: obj.elevation,
            range: obj.range,
          };
        });

        // Generate range rings
        const rangeRings: any[] = [];
        if (showRangeRings) {
          [0.25, 0.5, 0.75, 1.0].forEach((fraction) => {
            const r = fraction * 100;
            const ringPoints = Array.from({ length: 73 }, (_, i) => {
              const angle = (i / 72) * Math.PI * 2;
              return [r * Math.sin(angle), 0, r * Math.cos(angle)];
            });
            rangeRings.push({
              type: "line3D",
              coordinateSystem: "cartesian3D",
              data: ringPoints,
              lineStyle: { color: "rgba(100, 116, 139, 0.3)", width: 1 },
              silent: true,
            });
          });
        }

        return {
          tooltip: {
            show: true,
            ...tooltipStyle,
            formatter: ((params: any) => {
              const d = params.data || {};
              let html = `<div style="font-weight:600;font-size:13px;margin-bottom:4px">${d.name}</div>`;
              html += `<div style="color:rgba(255,255,255,0.5);text-transform:uppercase;font-size:10px;margin-bottom:6px">${d.category || "Unknown"}</div>`;
              html += `<div>Azimuth: <span style="font-family:monospace">${d.azimuth?.toFixed(1)}°</span></div>`;
              html += `<div>Elevation: <span style="font-family:monospace">${d.elevation?.toFixed(1)}°</span></div>`;
              html += `<div style="color:#22d3ee">Range: <span style="font-family:monospace">${d.range?.toFixed(1)} km</span></div>`;
              return html;
            }) as unknown as string,
          },
          grid3D: {
            boxWidth: 100,
            boxHeight: 50,
            boxDepth: 100,
            viewControl: {
              autoRotate,
              autoRotateSpeed: 2,
              distance: 250,
              alpha: 25,
              beta: 45,
              rotateSensitivity: 2,
              zoomSensitivity: 1.5,
            },
            light: {
              main: { intensity: 1.2, shadow: false },
              ambient: { intensity: 0.5 },
            },
            postEffect: {
              enable: true,
              bloom: { enable: true, intensity: 0.05 },
            },
          },
          xAxis3D: {
            type: "value",
            name: "East",
            min: -100,
            max: 100,
            axisLabel: { show: false },
            splitLine: { show: false },
          },
          yAxis3D: {
            type: "value",
            name: "Up",
            min: -50,
            max: 100,
            axisLabel: { show: false },
            splitLine: { show: false },
          },
          zAxis3D: {
            type: "value",
            name: "North",
            min: -100,
            max: 100,
            axisLabel: { show: false },
            splitLine: { show: false },
          },
          series: [
            ...rangeRings,
            {
              type: "scatter3D",
              coordinateSystem: "cartesian3D",
              data: scatterData,
              symbolSize: 12,
            },
          ],
        };
      }, [
        data,
        maxRange,
        showRangeRings,
        autoRotate,
        categoryColors,
        tooltipStyle,
      ]);

      if (!glReady)
        return (
          <AstroChart
            ref={ref}
            type="scatter"
            series={[]}
            title={title}
            width={width}
            height={height}
            loading
            className={className}
          />
        );
      return (
        <AstroChart
          ref={ref}
          type="scatter"
          series={[]}
          title={title}
          width={width}
          height={height}
          loading={loading}
          className={className}
          echartsOptions={echartsOptions as any}
          export={exportOptions}
          infoTooltip={infoTooltip}
        />
      );
    },
  ),
);

// =============================================================================
// TransferOrbit3DChart - Mission Planning & Trajectory Visualization
// =============================================================================

export interface TransferOrbitSegment {
  id: string;
  name: string;
  path: Array<{ x: number; y: number; z: number }>;
  type: "coast" | "burn" | "gravity-assist" | "spiral";
  deltaV?: number;
  color?: string;
}

export interface TransferOrbitWaypoint {
  name: string;
  position: { x: number; y: number; z: number };
  type: "departure" | "arrival" | "flyby" | "node" | "burn";
  deltaV?: number;
  tof?: number;
}

export interface TransferOrbit3DChartProps extends CommonChartProps {
  segments: TransferOrbitSegment[];
  waypoints?: TransferOrbitWaypoint[];
  referenceOrbits?: Array<{
    name: string;
    path: Array<{ x: number; y: number; z: number }>;
    color?: string;
  }>;
  title?: string;
  width?: number | string;
  height?: number;
  showDeltaV?: boolean;
  autoRotate?: boolean;
  loading?: boolean;
  className?: string;
}

/**
 * TransferOrbit3DChart - Interplanetary/orbital transfer visualization
 *
 * USE CASES:
 * - Hohmann transfer orbit planning
 * - Bi-elliptic transfer visualization
 * - Gravity assist trajectory design
 * - Low-thrust spiral maneuvers
 * - Lunar/Mars mission trajectory planning
 */
export const TransferOrbit3DChart = memo(
  forwardRef<AstroChartHandle, TransferOrbit3DChartProps>(
    function TransferOrbit3DChart(
      {
        segments = [],
        waypoints = [],
        referenceOrbits = [],
        title,
        width = "100%",
        height = 550,
        showDeltaV = true,
        autoRotate = false,
        loading = false,
        className = "",
        export: exportOptions,
        infoTooltip,
      },
      ref,
    ) {
      const glReady = useEchartsGlReady();
      const tooltipStyle = useChartTooltipStyle();
      const legendStyle = useChartLegendStyle();
      const { tokens, mode } = useTheme();
      const labelPrimary = tokens.colors.text.primary;
      const labelSecondary = tokens.colors.text.secondary;
      const labelTertiary = tokens.colors.text.tertiary;
      const labelBg =
        mode === "dark" ? "rgba(15, 23, 42, 0.72)" : "rgba(255, 255, 255, 0.9)";

      const segmentColors: Record<string, string> = {
        coast: "#9D70FF",
        burn: "#ff3838",
        "gravity-assist": "#22d3ee",
        spiral: "#56f000",
      };
      const waypointColors: Record<string, string> = {
        departure: "#3E3CFF",
        arrival: "#56f000",
        flyby: "#22d3ee",
        node: "#fce83a",
        burn: "#ff3838",
      };

      const echartsOptions = useMemo(() => {
        const seriesArray: any[] = [];
        referenceOrbits.forEach((orbit) => {
          seriesArray.push({
            type: "line3D",
            name: orbit.name,
            coordinateSystem: "cartesian3D",
            data: orbit.path.map((p) => [p.x, p.y, p.z]),
            lineStyle: {
              color: orbit.color || "rgba(100, 116, 139, 0.3)",
              width: 1,
              type: "dashed",
            },
            silent: true,
          });
        });
        segments.forEach((segment, idx) => {
          const color =
            segment.color ||
            segmentColors[segment.type] ||
            ASTRO_DATA_VIZ_COLORS.mixed[
              idx % ASTRO_DATA_VIZ_COLORS.mixed.length
            ];
          seriesArray.push({
            type: "line3D",
            name: segment.name,
            coordinateSystem: "cartesian3D",
            data: segment.path.map((p) => [p.x, p.y, p.z]),
            lineStyle: { color, width: segment.type === "burn" ? 4 : 2 },
          });
        });
        if (waypoints.length > 0) {
          seriesArray.push({
            type: "scatter3D",
            name: "Waypoints",
            coordinateSystem: "cartesian3D",
            data: waypoints.map((wp) => ({
              name: wp.name,
              value: [wp.position.x, wp.position.y, wp.position.z],
              itemStyle: { color: waypointColors[wp.type] || "#fff" },
              symbolSize: wp.type === "burn" ? 14 : 18,
              deltaV: wp.deltaV,
              tof: wp.tof,
              waypointType: wp.type,
            })),
            label: {
              show: true,
              formatter: (p: any) => p.data?.name,
              textStyle: {
                color: labelPrimary,
                fontSize: 11,
                backgroundColor: labelBg,
                padding: [2, 4],
              },
            },
          });
        }
        const totalDeltaV =
          segments.reduce((sum, s) => sum + (s.deltaV || 0), 0) +
          waypoints.reduce((sum, w) => sum + (w.deltaV || 0), 0);
        return {
          tooltip: {
            show: true,
            ...tooltipStyle,
            formatter: ((params: any) => {
              const d = params.data || {};
              if (params.seriesType === "line3D") {
                const seg = segments.find((s) => s.name === params.seriesName);
                return `<div style="font-weight:600">${params.seriesName}</div>${seg?.deltaV ? `<div style="color:#ff3838">ΔV: ${seg.deltaV.toFixed(2)} km/s</div>` : ""}`;
              }
              return `<div style="font-weight:600;margin-bottom:4px">${d.name}</div>${d.deltaV ? `<div style="color:#ff3838">ΔV: ${d.deltaV.toFixed(2)} km/s</div>` : ""}${d.tof ? `<div>TOF: ${d.tof.toFixed(0)} days</div>` : ""}`;
            }) as unknown as string,
          },
          legend: { show: true, top: 10, ...legendStyle },
          graphic:
            showDeltaV && totalDeltaV > 0
              ? [
                  {
                    type: "text",
                    right: 20,
                    top: 20,
                    style: {
                      text: `Total ΔV: ${totalDeltaV.toFixed(2)} km/s`,
                      fill: "#ff3838",
                      fontSize: 14,
                      fontWeight: "bold",
                    },
                  },
                ]
              : undefined,
          grid3D: {
            boxWidth: 150,
            boxHeight: 80,
            boxDepth: 150,
            viewControl: {
              autoRotate,
              autoRotateSpeed: 2,
              distance: 280,
              alpha: 20,
              beta: 40,
            },
            light: { main: { intensity: 1.2 }, ambient: { intensity: 0.4 } },
            postEffect: {
              enable: true,
              bloom: { enable: true, intensity: 0.08 },
            },
          },
          xAxis3D: {
            type: "value",
            name: "X (AU)",
            nameTextStyle: { color: labelSecondary },
            axisLabel: {
              color: labelTertiary,
              formatter: (v: number) => v.toFixed(1),
            },
            splitLine: {
              show: true,
              lineStyle: { color: "rgba(100, 116, 139, 0.1)" },
            },
          },
          yAxis3D: {
            type: "value",
            name: "Z (AU)",
            nameTextStyle: { color: labelSecondary },
            axisLabel: {
              color: labelTertiary,
              formatter: (v: number) => v.toFixed(1),
            },
            splitLine: {
              show: true,
              lineStyle: { color: "rgba(100, 116, 139, 0.1)" },
            },
          },
          zAxis3D: {
            type: "value",
            name: "Y (AU)",
            nameTextStyle: { color: labelSecondary },
            axisLabel: {
              color: labelTertiary,
              formatter: (v: number) => v.toFixed(1),
            },
            splitLine: {
              show: true,
              lineStyle: { color: "rgba(100, 116, 139, 0.1)" },
            },
          },
          series: seriesArray,
        };
      }, [
        segments,
        waypoints,
        referenceOrbits,
        showDeltaV,
        autoRotate,
        segmentColors,
        waypointColors,
        tooltipStyle,
        legendStyle,
        labelPrimary,
        labelSecondary,
        labelTertiary,
        labelBg,
      ]);

      if (!glReady)
        return (
          <AstroChart
            ref={ref}
            type="scatter"
            series={[]}
            title={title}
            width={width}
            height={height}
            loading
            className={className}
          />
        );
      return (
        <AstroChart
          ref={ref}
          type="scatter"
          series={[]}
          title={title}
          width={width}
          height={height}
          loading={loading}
          className={className}
          echartsOptions={echartsOptions as any}
          export={exportOptions}
          infoTooltip={infoTooltip}
        />
      );
    },
  ),
);

// =============================================================================
// AttitudeHistory3DChart - Spacecraft Orientation Over Time
// =============================================================================

export interface AttitudeHistoryPoint {
  time: number;
  euler?: { roll: number; pitch: number; yaw: number };
  error?: number;
  mode?: string;
}

export interface AttitudeHistory3DChartProps extends CommonChartProps {
  data: AttitudeHistoryPoint[];
  title?: string;
  width?: number | string;
  height?: number;
  mode?: "euler" | "pointing-error";
  showModeTransitions?: boolean;
  autoRotate?: boolean;
  loading?: boolean;
  className?: string;
}

/**
 * AttitudeHistory3DChart - Spacecraft attitude evolution visualization
 *
 * USE CASES:
 * - Attitude maneuver visualization
 * - Tumbling/spin analysis
 * - Pointing stability assessment
 * - Safe mode recovery analysis
 * - Slew profile optimization
 */
export const AttitudeHistory3DChart = memo(
  forwardRef<AstroChartHandle, AttitudeHistory3DChartProps>(
    function AttitudeHistory3DChart(
      {
        data = [],
        title,
        width = "100%",
        height = 500,
        mode = "euler",
        showModeTransitions = true,
        autoRotate = false,
        loading = false,
        className = "",
        export: exportOptions,
        infoTooltip,
      },
      ref,
    ) {
      const glReady = useEchartsGlReady();
      const tooltipStyle = useChartTooltipStyle();
      const legendStyle = useChartLegendStyle();

      const echartsOptions = useMemo(() => {
        const seriesArray: any[] = [];
        if (mode === "euler" && data.length > 0) {
          const pathData = data
            .filter((d) => d.euler)
            .map((d) => [d.euler!.roll, d.euler!.pitch, d.euler!.yaw]);
          seriesArray.push({
            type: "line3D",
            name: "Attitude Path",
            coordinateSystem: "cartesian3D",
            data: pathData,
            lineStyle: { color: "#3E3CFF", width: 2 },
          });
          if (pathData.length > 0) {
            seriesArray.push({
              type: "scatter3D",
              name: "Start/End",
              coordinateSystem: "cartesian3D",
              data: [
                {
                  value: pathData[0],
                  name: "Start",
                  itemStyle: { color: "#56f000" },
                },
                {
                  value: pathData[pathData.length - 1],
                  name: "End",
                  itemStyle: { color: "#ff3838" },
                },
              ],
              symbolSize: 14,
            });
          }
          if (showModeTransitions) {
            const transitions = data.filter(
              (d, i) => i > 0 && d.mode !== data[i - 1].mode && d.euler,
            );
            if (transitions.length > 0) {
              seriesArray.push({
                type: "scatter3D",
                name: "Mode Changes",
                coordinateSystem: "cartesian3D",
                data: transitions.map((d) => ({
                  value: [d.euler!.roll, d.euler!.pitch, d.euler!.yaw],
                  name: d.mode,
                  itemStyle: { color: "#fce83a" },
                })),
                symbolSize: 10,
                symbol: "diamond",
              });
            }
          }
        }
        return {
          tooltip: {
            show: true,
            ...tooltipStyle,
            formatter: ((params: any) => {
              const d = params.data || {};
              const [roll, pitch, yaw] = d.value || [0, 0, 0];
              return `<div style="font-weight:600;margin-bottom:4px">${d.name || params.seriesName}</div><div>Roll: ${roll.toFixed(2)}°</div><div>Pitch: ${pitch.toFixed(2)}°</div><div>Yaw: ${yaw.toFixed(2)}°</div>`;
            }) as unknown as string,
          },
          legend: { show: true, top: 10, ...legendStyle },
          grid3D: {
            boxWidth: 100,
            boxHeight: 100,
            boxDepth: 100,
            viewControl: { autoRotate, distance: 220, alpha: 25, beta: 45 },
            light: { main: { intensity: 1.2 }, ambient: { intensity: 0.4 } },
          },
          xAxis3D: {
            type: "value",
            name: "Roll (°)",
            nameTextStyle: { color: "rgba(255, 255, 255, 0.5)" },
            axisLabel: { color: "rgba(255, 255, 255, 0.4)" },
            splitLine: {
              show: true,
              lineStyle: { color: "rgba(100, 116, 139, 0.1)" },
            },
          },
          yAxis3D: {
            type: "value",
            name: "Pitch (°)",
            nameTextStyle: { color: "rgba(255, 255, 255, 0.5)" },
            axisLabel: { color: "rgba(255, 255, 255, 0.4)" },
            splitLine: {
              show: true,
              lineStyle: { color: "rgba(100, 116, 139, 0.1)" },
            },
          },
          zAxis3D: {
            type: "value",
            name: "Yaw (°)",
            nameTextStyle: { color: "rgba(255, 255, 255, 0.5)" },
            axisLabel: { color: "rgba(255, 255, 255, 0.4)" },
            splitLine: {
              show: true,
              lineStyle: { color: "rgba(100, 116, 139, 0.1)" },
            },
          },
          series: seriesArray,
        };
      }, [
        data,
        mode,
        showModeTransitions,
        autoRotate,
        tooltipStyle,
        legendStyle,
      ]);
      if (!glReady)
        return (
          <AstroChart
            ref={ref}
            type="scatter"
            series={[]}
            title={title}
            width={width}
            height={height}
            loading
            className={className}
          />
        );
      return (
        <AstroChart
          ref={ref}
          type="scatter"
          series={[]}
          title={title}
          width={width}
          height={height}
          loading={loading}
          className={className}
          echartsOptions={echartsOptions as any}
          export={exportOptions}
          infoTooltip={infoTooltip}
        />
      );
    },
  ),
);

// =============================================================================
// ConjunctionAssessment3DChart - Collision Analysis
// =============================================================================

export interface ConjunctionEvent {
  id: string;
  primary: string;
  secondary: string;
  tca: number;
  missDistance: number;
  probability?: number;
  relativeVelocity?: number;
  position: { x: number; y: number; z: number };
  uncertainty?: { rx: number; ry: number; rz: number };
}

export interface ConjunctionAssessment3DChartProps extends CommonChartProps {
  events: ConjunctionEvent[];
  primaryOrbit?: Array<{ x: number; y: number; z: number }>;
  title?: string;
  width?: number | string;
  height?: number;
  showUncertainty?: boolean;
  warningThreshold?: number;
  criticalThreshold?: number;
  autoRotate?: boolean;
  loading?: boolean;
  className?: string;
}

/**
 * ConjunctionAssessment3DChart - Space object collision analysis
 *
 * USE CASES:
 * - Conjunction screening results visualization
 * - Collision avoidance maneuver planning
 * - Debris close approach monitoring
 * - Rendezvous safety analysis
 * - Formation flying separation monitoring
 */
export const ConjunctionAssessment3DChart = memo(
  forwardRef<AstroChartHandle, ConjunctionAssessment3DChartProps>(
    function ConjunctionAssessment3DChart(
      {
        events = [],
        primaryOrbit = [],
        title,
        width = "100%",
        height = 550,
        showUncertainty = true,
        warningThreshold = 5,
        criticalThreshold = 1,
        autoRotate = false,
        loading = false,
        className = "",
        export: exportOptions,
        infoTooltip,
      },
      ref,
    ) {
      const glReady = useEchartsGlReady();
      const tooltipStyle = useChartTooltipStyle();
      const legendStyle = useChartLegendStyle();
      const { tokens, mode } = useTheme();
      const _labelPrimary = tokens.colors.text.primary;
      const _labelSecondary = tokens.colors.text.secondary;
      const _labelTertiary = tokens.colors.text.tertiary;
      const _labelBg =
        mode === "dark" ? "rgba(15, 23, 42, 0.72)" : "rgba(255, 255, 255, 0.9)";

      const echartsOptions = useMemo(() => {
        const seriesArray: any[] = [];
        if (primaryOrbit && primaryOrbit.length > 0) {
          seriesArray.push({
            type: "line3D",
            name: "Primary Orbit",
            coordinateSystem: "cartesian3D",
            data: primaryOrbit.map((p) => [p.x, p.y, p.z]),
            lineStyle: { color: "#3E3CFF", width: 2 },
          });
        }
        const eventData = events.map((evt) => {
          const color =
            evt.missDistance < criticalThreshold
              ? "#ff3838"
              : evt.missDistance < warningThreshold
                ? "#fce83a"
                : "#56f000";
          return {
            name: `${evt.primary} × ${evt.secondary}`,
            value: [evt.position.x, evt.position.y, evt.position.z],
            itemStyle: { color },
            symbolSize: Math.max(8, Math.min(24, 30 - evt.missDistance * 3)),
            ...evt,
          };
        });
        seriesArray.push({
          type: "scatter3D",
          name: "Conjunctions",
          coordinateSystem: "cartesian3D",
          data: eventData,
          symbol: "circle",
          emphasis: { itemStyle: { borderColor: "#fff", borderWidth: 2 } },
        });
        if (showUncertainty) {
          events
            .filter((e) => e.uncertainty)
            .forEach((evt) => {
              const { x, y, z } = evt.position;
              const { rx, ry, rz } = evt.uncertainty!;
              seriesArray.push({
                type: "line3D",
                coordinateSystem: "cartesian3D",
                data: [
                  [x - rx, y, z],
                  [x + rx, y, z],
                ],
                lineStyle: { color: "rgba(255, 255, 255, 0.3)", width: 1 },
                silent: true,
              });
              seriesArray.push({
                type: "line3D",
                coordinateSystem: "cartesian3D",
                data: [
                  [x, y - ry, z],
                  [x, y + ry, z],
                ],
                lineStyle: { color: "rgba(255, 255, 255, 0.3)", width: 1 },
                silent: true,
              });
              seriesArray.push({
                type: "line3D",
                coordinateSystem: "cartesian3D",
                data: [
                  [x, y, z - rz],
                  [x, y, z + rz],
                ],
                lineStyle: { color: "rgba(255, 255, 255, 0.3)", width: 1 },
                silent: true,
              });
            });
        }
        return {
          tooltip: {
            show: true,
            ...tooltipStyle,
            formatter: ((params: any) => {
              const d = params.data || {};
              if (params.seriesType === "line3D") return "";
              const mdColor =
                d.missDistance < criticalThreshold
                  ? "#ff3838"
                  : d.missDistance < warningThreshold
                    ? "#fce83a"
                    : "#56f000";
              return `<div style="font-weight:600;margin-bottom:4px">${d.name}</div><div style="color:${mdColor}">Miss Distance: ${d.missDistance?.toFixed(3)} km</div>${d.probability !== undefined ? `<div>Probability: ${(d.probability * 100).toExponential(2)}%</div>` : ""}${d.relativeVelocity ? `<div>Rel. Velocity: ${d.relativeVelocity.toFixed(2)} km/s</div>` : ""}`;
            }) as unknown as string,
          },
          legend: { show: true, top: 10, ...legendStyle },
          grid3D: {
            boxWidth: 100,
            boxHeight: 100,
            boxDepth: 100,
            viewControl: { autoRotate, distance: 220, alpha: 25, beta: 45 },
            light: { main: { intensity: 1.2 }, ambient: { intensity: 0.4 } },
            postEffect: {
              enable: true,
              bloom: { enable: true, intensity: 0.05 },
            },
          },
          xAxis3D: {
            type: "value",
            name: "X (km)",
            nameTextStyle: { color: "rgba(255, 255, 255, 0.5)" },
            axisLabel: { color: "rgba(255, 255, 255, 0.4)" },
            splitLine: {
              show: true,
              lineStyle: { color: "rgba(100, 116, 139, 0.1)" },
            },
          },
          yAxis3D: {
            type: "value",
            name: "Y (km)",
            nameTextStyle: { color: "rgba(255, 255, 255, 0.5)" },
            axisLabel: { color: "rgba(255, 255, 255, 0.4)" },
            splitLine: {
              show: true,
              lineStyle: { color: "rgba(100, 116, 139, 0.1)" },
            },
          },
          zAxis3D: {
            type: "value",
            name: "Z (km)",
            nameTextStyle: { color: "rgba(255, 255, 255, 0.5)" },
            axisLabel: { color: "rgba(255, 255, 255, 0.4)" },
            splitLine: {
              show: true,
              lineStyle: { color: "rgba(100, 116, 139, 0.1)" },
            },
          },
          series: seriesArray,
        };
      }, [
        events,
        primaryOrbit,
        showUncertainty,
        warningThreshold,
        criticalThreshold,
        autoRotate,
        tooltipStyle,
        legendStyle,
      ]);
      if (!glReady)
        return (
          <AstroChart
            ref={ref}
            type="scatter"
            series={[]}
            title={title}
            width={width}
            height={height}
            loading
            className={className}
          />
        );
      return (
        <AstroChart
          ref={ref}
          type="scatter"
          series={[]}
          title={title}
          width={width}
          height={height}
          loading={loading}
          className={className}
          echartsOptions={echartsOptions as any}
          export={exportOptions}
          infoTooltip={infoTooltip}
        />
      );
    },
  ),
);

// =============================================================================
// LaunchCorridor3DChart - Launch/Reentry Visualization
// =============================================================================

export interface LaunchCorridorData {
  corridors: Array<{
    id: string;
    name: string;
    type: "nominal" | "abort" | "safety" | "exclusion";
    groundFootprint: Array<{ lat: number; lon: number }>;
    altitudeRange: [number, number];
    color?: string;
  }>;
  trajectory?: Array<{
    lat: number;
    lon: number;
    altitude: number;
    time?: number;
  }>;
  events?: Array<{
    name: string;
    lat: number;
    lon: number;
    altitude: number;
    time?: number;
  }>;
}

export interface LaunchCorridor3DChartProps extends CommonChartProps {
  data: LaunchCorridorData;
  title?: string;
  width?: number | string;
  height?: number;
  showTrajectory?: boolean;
  autoRotate?: boolean;
  loading?: boolean;
  className?: string;
}

/**
 * LaunchCorridor3DChart - Launch/reentry corridor visualization
 *
 * USE CASES:
 * - Launch window planning
 * - Range safety corridor definition
 * - Abort trajectory visualization
 * - Reentry footprint prediction
 * - Debris fall zone analysis
 */
export const LaunchCorridor3DChart = memo(
  forwardRef<AstroChartHandle, LaunchCorridor3DChartProps>(
    function LaunchCorridor3DChart(
      {
        data = { corridors: [] } as LaunchCorridorData,
        title,
        width = "100%",
        height = 550,
        showTrajectory = true,
        autoRotate = false,
        loading = false,
        className = "",
        export: exportOptions,
        infoTooltip,
      },
      ref,
    ) {
      const glReady = useEchartsGlReady();
      const { tokens, mode } = useTheme();
      const tooltipStyle = useChartTooltipStyle();
      const labelPrimary = tokens.colors.text.primary;
      const labelSecondary = tokens.colors.text.secondary;
      const labelTertiary = tokens.colors.text.tertiary;
      const labelBg =
        mode === "dark" ? "rgba(15, 23, 42, 0.72)" : "rgba(255, 255, 255, 0.9)";
      const legendStyle = useChartLegendStyle();

      const corridorColors: Record<string, string> = {
        nominal: "rgba(86, 240, 0, 0.4)",
        abort: "rgba(255, 179, 2, 0.4)",
        safety: "rgba(34, 211, 238, 0.4)",
        exclusion: "rgba(255, 56, 56, 0.4)",
      };
      const echartsOptions = useMemo(() => {
        const seriesArray: any[] = [];
        const latLonTo3D = (lat: number, lon: number, alt: number) => [
          lon,
          alt * 0.1,
          lat,
        ];
        data.corridors.forEach((corridor) => {
          const color =
            corridor.color ||
            corridorColors[corridor.type] ||
            "rgba(100, 116, 139, 0.4)";
          const lowerPath = corridor.groundFootprint.map((p) =>
            latLonTo3D(p.lat, p.lon, corridor.altitudeRange[0]),
          );
          if (lowerPath.length > 0) lowerPath.push(lowerPath[0]);
          seriesArray.push({
            type: "line3D",
            name: `${corridor.name} (floor)`,
            coordinateSystem: "cartesian3D",
            data: lowerPath,
            lineStyle: { color, width: 2 },
          });
          const upperPath = corridor.groundFootprint.map((p) =>
            latLonTo3D(p.lat, p.lon, corridor.altitudeRange[1]),
          );
          if (upperPath.length > 0) upperPath.push(upperPath[0]);
          seriesArray.push({
            type: "line3D",
            name: `${corridor.name} (ceiling)`,
            coordinateSystem: "cartesian3D",
            data: upperPath,
            lineStyle: { color, width: 2, type: "dashed" },
          });
        });
        if (showTrajectory && data.trajectory && data.trajectory.length > 0) {
          seriesArray.push({
            type: "line3D",
            name: "Trajectory",
            coordinateSystem: "cartesian3D",
            data: data.trajectory.map((p) =>
              latLonTo3D(p.lat, p.lon, p.altitude),
            ),
            lineStyle: { color: "#fff", width: 3 },
          });
        }
        if (data.events && data.events.length > 0) {
          seriesArray.push({
            type: "scatter3D",
            name: "Events",
            coordinateSystem: "cartesian3D",
            data: data.events.map((e) => ({
              name: e.name,
              value: latLonTo3D(e.lat, e.lon, e.altitude),
              itemStyle: { color: "#fce83a" },
              altitude: e.altitude,
              time: e.time,
            })),
            symbolSize: 12,
            symbol: "diamond",
            label: {
              show: true,
              formatter: (p: any) => p.data?.name,
              textStyle: {
                color: labelPrimary,
                fontSize: 10,
                backgroundColor: labelBg,
                padding: [2, 4],
              },
            },
          });
        }
        return {
          tooltip: {
            show: true,
            ...tooltipStyle,
            formatter: ((params: any) => {
              const d = params.data || {};
              if (params.seriesType === "line3D")
                return `<div style="font-weight:600">${params.seriesName}</div>`;
              return `<div style="font-weight:600;margin-bottom:4px">${d.name}</div>${d.altitude ? `<div>Altitude: ${d.altitude.toFixed(1)} km</div>` : ""}${d.time ? `<div>T+: ${d.time.toFixed(0)}s</div>` : ""}`;
            }) as unknown as string,
          },
          legend: { show: true, top: 10, ...legendStyle },
          grid3D: {
            boxWidth: 150,
            boxHeight: 60,
            boxDepth: 100,
            viewControl: { autoRotate, distance: 280, alpha: 30, beta: 30 },
            light: { main: { intensity: 1.2 }, ambient: { intensity: 0.4 } },
          },
          xAxis3D: {
            type: "value",
            name: "Longitude (°)",
            nameTextStyle: { color: labelSecondary },
            axisLabel: { color: labelTertiary },
            splitLine: {
              show: true,
              lineStyle: { color: "rgba(100, 116, 139, 0.1)" },
            },
          },
          yAxis3D: {
            type: "value",
            name: "Altitude (km)",
            nameTextStyle: { color: labelSecondary },
            axisLabel: { color: labelTertiary },
            splitLine: {
              show: true,
              lineStyle: { color: "rgba(100, 116, 139, 0.1)" },
            },
          },
          zAxis3D: {
            type: "value",
            name: "Latitude (°)",
            nameTextStyle: { color: labelSecondary },
            axisLabel: { color: labelTertiary },
            splitLine: {
              show: true,
              lineStyle: { color: "rgba(100, 116, 139, 0.1)" },
            },
          },
          series: seriesArray,
        };
      }, [
        data,
        showTrajectory,
        autoRotate,
        corridorColors,
        tooltipStyle,
        legendStyle,
        labelPrimary,
        labelSecondary,
        labelTertiary,
        labelBg,
      ]);
      if (!glReady)
        return (
          <AstroChart
            ref={ref}
            type="scatter"
            series={[]}
            title={title}
            width={width}
            height={height}
            loading
            className={className}
          />
        );
      return (
        <AstroChart
          ref={ref}
          type="scatter"
          series={[]}
          title={title}
          width={width}
          height={height}
          loading={loading}
          className={className}
          echartsOptions={echartsOptions as any}
          export={exportOptions}
          infoTooltip={infoTooltip}
        />
      );
    },
  ),
);

// =============================================================================
// AntennaPattern3DChart - RF/Communications Pattern Visualization
// =============================================================================

export interface AntennaPattern3DData {
  name: string;
  pattern: Array<{ theta: number; phi: number; gain: number }>;
  boresight?: { theta: number; phi: number };
  color?: string;
}

export interface AntennaPattern3DChartProps extends CommonChartProps {
  patterns: AntennaPattern3DData[];
  title?: string;
  width?: number | string;
  height?: number;
  showScale?: boolean;
  minGain?: number;
  autoRotate?: boolean;
  loading?: boolean;
  className?: string;
}

/**
 * AntennaPattern3DChart - 3D antenna radiation pattern visualization
 *
 * USE CASES:
 * - Antenna gain pattern analysis
 * - Multi-beam satellite coverage
 * - Interference zone identification
 * - Link margin optimization
 * - Phased array beam steering visualization
 */
export const AntennaPattern3DChart = memo(
  forwardRef<AstroChartHandle, AntennaPattern3DChartProps>(
    function AntennaPattern3DChart(
      {
        patterns = [],
        title,
        width = "100%",
        height = 500,
        minGain = -20,
        autoRotate = true,
        loading = false,
        className = "",
        export: exportOptions,
        infoTooltip,
      },
      ref,
    ) {
      const glReady = useEchartsGlReady();
      const { tokens } = useTheme();
      const tooltipStyle = useChartTooltipStyle();
      const _labelPrimary = tokens.colors.text.primary;
      const _labelSecondary = tokens.colors.text.secondary;
      const legendStyle = useChartLegendStyle();

      const echartsOptions = useMemo(() => {
        const seriesArray: any[] = [];
        patterns.forEach((pattern, pIdx) => {
          const color =
            pattern.color ||
            ASTRO_DATA_VIZ_COLORS.mixed[
              pIdx % ASTRO_DATA_VIZ_COLORS.mixed.length
            ];
          const maxGain = Math.max(...pattern.pattern.map((p) => p.gain), 0);
          const surfaceData = pattern.pattern
            .filter((p) => p.gain >= minGain)
            .map((p) => {
              const thetaRad = (p.theta * Math.PI) / 180;
              const phiRad = (p.phi * Math.PI) / 180;
              const r = Math.max(
                0,
                ((p.gain - minGain) / (maxGain - minGain)) * 80 + 20,
              );
              return {
                value: [
                  r * Math.sin(thetaRad) * Math.cos(phiRad),
                  r * Math.cos(thetaRad),
                  r * Math.sin(thetaRad) * Math.sin(phiRad),
                ],
                gain: p.gain,
                itemStyle: { color, opacity: 0.7 },
              };
            });
          seriesArray.push({
            type: "scatter3D",
            name: pattern.name,
            coordinateSystem: "cartesian3D",
            data: surfaceData,
            symbolSize: 4,
          });
          if (pattern.boresight) {
            const thetaRad = (pattern.boresight.theta * Math.PI) / 180;
            const phiRad = (pattern.boresight.phi * Math.PI) / 180;
            seriesArray.push({
              type: "line3D",
              name: `${pattern.name} Boresight`,
              coordinateSystem: "cartesian3D",
              data: [
                [0, 0, 0],
                [
                  100 * Math.sin(thetaRad) * Math.cos(phiRad),
                  100 * Math.cos(thetaRad),
                  100 * Math.sin(thetaRad) * Math.sin(phiRad),
                ],
              ],
              lineStyle: { color: "#fff", width: 2 },
            });
          }
        });
        return {
          tooltip: {
            show: true,
            ...tooltipStyle,
            formatter: ((params: any) => {
              const d = params.data || {};
              return `<div style="font-weight:600;margin-bottom:4px">${params.seriesName}</div>${d.gain !== undefined ? `<div style="color:#22d3ee">Gain: ${d.gain.toFixed(1)} dBi</div>` : ""}`;
            }) as unknown as string,
          },
          legend: { show: patterns.length > 1, top: 10, ...legendStyle },
          grid3D: {
            boxWidth: 100,
            boxHeight: 100,
            boxDepth: 100,
            viewControl: {
              autoRotate,
              autoRotateSpeed: 3,
              distance: 220,
              alpha: 20,
              beta: 45,
            },
            light: { main: { intensity: 1.2 }, ambient: { intensity: 0.5 } },
            postEffect: {
              enable: true,
              bloom: { enable: true, intensity: 0.08 },
            },
          },
          xAxis3D: {
            type: "value",
            name: "X",
            min: -100,
            max: 100,
            axisLabel: { show: false },
            splitLine: { show: false },
          },
          yAxis3D: {
            type: "value",
            name: "Z",
            min: -100,
            max: 100,
            axisLabel: { show: false },
            splitLine: { show: false },
          },
          zAxis3D: {
            type: "value",
            name: "Y",
            min: -100,
            max: 100,
            axisLabel: { show: false },
            splitLine: { show: false },
          },
          series: seriesArray,
        };
      }, [patterns, minGain, autoRotate, tooltipStyle, legendStyle]);
      if (!glReady)
        return (
          <AstroChart
            ref={ref}
            type="scatter"
            series={[]}
            title={title}
            width={width}
            height={height}
            loading
            className={className}
          />
        );
      return (
        <AstroChart
          ref={ref}
          type="scatter"
          series={[]}
          title={title}
          width={width}
          height={height}
          loading={loading}
          className={className}
          echartsOptions={echartsOptions as any}
          export={exportOptions}
          infoTooltip={infoTooltip}
        />
      );
    },
  ),
);

// =============================================================================
// WaterfallChart - RF Spectrum Waterfall/Spectrogram Display
// =============================================================================

import type {
  WaterfallDataPoint,
  WaterfallSignalMarker,
  DopplerTrackPoint,
  SatellitePassInfo,
  LinkMarginPoint,
} from "./types";

export interface WaterfallChartProps extends CommonChartProps {
  /** Waterfall data points */
  data: WaterfallDataPoint[];
  /** Chart title */
  title?: string;
  /** Center frequency (Hz) */
  centerFrequency?: number;
  /** Bandwidth (Hz) */
  bandwidth?: number;
  /** FFT bins */
  fftBins?: number;
  /** Color map: viridis, plasma, inferno, magma */
  colorMap?: "viridis" | "plasma" | "inferno" | "magma";
  /** Power range [min, max] in dBm */
  powerRange?: [number, number];
  /** Time window in seconds */
  timeWindow?: number;
  /** Signal markers to highlight */
  markers?: WaterfallSignalMarker[];
  /** Show spectrum line above heatmap */
  showSpectrum?: boolean;
  width?: number | string;
  height?: number;
  loading?: boolean;
  className?: string;
}

/**
 * WaterfallChart - RF spectrum waterfall/spectrogram display
 *
 * USE CASES:
 * - RF signal monitoring
 * - Interference detection
 * - Spectrum analysis
 * - Signal characterization
 */
export const WaterfallChart = memo(
  forwardRef<AstroChartHandle, WaterfallChartProps>(function WaterfallChart(
    {
      data = [],
      title = "RF Spectrum Waterfall",
      centerFrequency: _centerFrequency,
      bandwidth: _bandwidth,
      fftBins = 512,
      colorMap = "viridis",
      powerRange = [-120, -60],
      timeWindow = 60,
      markers: _markers = [],
      showSpectrum: _showSpectrum = true,
      width = "100%",
      height = 400,
      loading = false,
      className = "",
      export: exportOptions,
      infoTooltip,
    },
    ref,
  ) {
    const { tokens } = useTheme();
    const tooltipStyle = useChartTooltipStyle();
    const _labelPrimary = tokens.colors.text.primary;
    const _labelSecondary = tokens.colors.text.secondary;

    // Color maps for spectrum display
    const colorMaps: Record<string, string[]> = {
      viridis: [
        "#440154",
        "#482777",
        "#3e4989",
        "#31688e",
        "#26828e",
        "#1f9e89",
        "#35b779",
        "#6ece58",
        "#b5de2b",
        "#fde725",
      ],
      plasma: [
        "#0d0887",
        "#46039f",
        "#7201a8",
        "#9c179e",
        "#bd3786",
        "#d8576b",
        "#ed7953",
        "#fb9f3a",
        "#fdca26",
        "#f0f921",
      ],
      inferno: [
        "#000004",
        "#1b0c41",
        "#4a0c6b",
        "#781c6d",
        "#a52c60",
        "#cf4446",
        "#ed6925",
        "#fb9b06",
        "#f7d13d",
        "#fcffa4",
      ],
      magma: [
        "#000004",
        "#180f3d",
        "#440f76",
        "#721f81",
        "#9e2f7f",
        "#cd4071",
        "#f1605d",
        "#fd9668",
        "#feca8d",
        "#fcfdbf",
      ],
    };

    const echartsOptions = useMemo(() => {
      if (!data.length) return {};

      // Build heatmap data: [freqIndex, timeIndex, power]
      const heatmapData: [number, number, number][] = [];
      const timeLabels: string[] = [];
      const freqLabels: string[] = [];

      // Get frequency range from first data point
      const firstPoint = data[0];
      const freqMin = Math.min(...firstPoint.frequencies);
      const freqMax = Math.max(...firstPoint.frequencies);
      const freqStep = (freqMax - freqMin) / (fftBins - 1);

      // Build frequency labels
      for (let i = 0; i < fftBins; i++) {
        const freq = freqMin + i * freqStep;
        freqLabels.push((freq / 1e6).toFixed(2));
      }

      // Process each time slice
      data.slice(-timeWindow).forEach((point, tIdx) => {
        const time =
          point.time instanceof Date ? point.time : new Date(point.time);
        timeLabels.push(time.toLocaleTimeString());

        point.powers.forEach((power, fIdx) => {
          if (fIdx < fftBins) {
            heatmapData.push([fIdx, tIdx, power]);
          }
        });
      });

      return {
        tooltip: {
          show: true,
          ...tooltipStyle,
          formatter: (params: any): string => {
            if (Array.isArray(params)) return "";
            const [fIdx, tIdx, power] = params.data || [];
            const freq = freqLabels[fIdx] || "N/A";
            const time = timeLabels[tIdx] || "N/A";
            return `<div style="font-weight:600">Spectrum</div>
              <div>Frequency: ${freq} MHz</div>
              <div>Time: ${time}</div>
              <div>Power: ${power?.toFixed(1) ?? "N/A"} dBm</div>`;
          },
        },
        grid: { left: 60, right: 40, top: 40, bottom: 60 },
        xAxis: {
          type: "category",
          data: freqLabels,
          name: "Frequency (MHz)",
          nameLocation: "middle",
          nameGap: 35,
        },
        yAxis: {
          type: "category",
          data: timeLabels,
          name: "Time",
          nameLocation: "middle",
          nameGap: 45,
        },
        visualMap: {
          min: powerRange[0],
          max: powerRange[1],
          calculable: true,
          orient: "horizontal",
          left: "center",
          bottom: 0,
          // Enable hover linking - highlight only hovered range
          hoverLink: true,
          // Non-hovered regions get faded opacity
          outOfRange: {
            color: "rgba(128, 128, 128, 0.12)",
            opacity: 0.15,
          },
          inRange: { color: colorMaps[colorMap] || colorMaps.viridis },
          text: [`${powerRange[1]} dBm`, `${powerRange[0]} dBm`],
          textStyle: { color: "rgba(255, 255, 255, 0.7)" },
        },
        series: [
          {
            type: "heatmap",
            data: heatmapData,
            emphasis: { itemStyle: { borderColor: "#fff", borderWidth: 1 } },
          },
        ],
      };
    }, [data, fftBins, colorMap, powerRange, timeWindow, tooltipStyle]);

    return (
      <AstroChart
        ref={ref}
        type="heatmap"
        series={[]}
        title={title}
        width={width}
        height={height}
        loading={loading}
        className={className}
        echartsOptions={echartsOptions as any}
        export={exportOptions}
        infoTooltip={infoTooltip}
      />
    );
  }),
);

// =============================================================================
// DopplerTrackChart - Doppler Shift Tracking Display
// =============================================================================

export interface DopplerTrackChartProps extends CommonChartProps {
  /** Predicted Doppler track */
  predicted: DopplerTrackPoint[];
  /** Measured Doppler data (optional) */
  measured?: DopplerTrackPoint[];
  /** Carrier frequency (Hz) */
  carrierFrequency?: number;
  /** Pass info */
  passInfo?: SatellitePassInfo;
  /** Show residuals (predicted - measured) */
  showResiduals?: boolean;
  /** Show elevation overlay */
  showElevation?: boolean;
  title?: string;
  width?: number | string;
  height?: number;
  loading?: boolean;
  className?: string;
}

/**
 * DopplerTrackChart - Doppler shift tracking visualization
 *
 * USE CASES:
 * - Satellite pass tracking
 * - Signal acquisition validation
 * - Orbit determination
 * - Tracking residual analysis
 */
export const DopplerTrackChart = memo(
  forwardRef<AstroChartHandle, DopplerTrackChartProps>(
    function DopplerTrackChart(
      {
        predicted = [],
        measured = [],
        carrierFrequency: _carrierFrequency,
        passInfo,
        showResiduals = false,
        showElevation = true,
        title,
        width = "100%",
        height = 300,
        loading = false,
        className = "",
        export: exportOptions,
        infoTooltip,
      },
      ref,
    ) {
      const tooltipStyle = useChartTooltipStyle();

      const chartTitle =
        title ||
        (passInfo ? `Doppler Track – ${passInfo.name}` : "Doppler Track");

      const echartsOptions = useMemo(() => {
        const series: any[] = [];

        // Predicted Doppler
        if (predicted.length > 0) {
          series.push({
            name: "Predicted",
            type: "line",
            data: predicted.map((p) => {
              const time = p.time instanceof Date ? p.time.getTime() : p.time;
              return [time, p.dopplerShift];
            }),
            lineStyle: { color: ASTRO_DATA_VIZ_COLORS.mixed[0], width: 2 },
            symbol: "none",
            smooth: true,
          });
        }

        // Measured Doppler
        if (measured.length > 0) {
          series.push({
            name: "Measured",
            type: "scatter",
            data: measured.map((p) => {
              const time = p.time instanceof Date ? p.time.getTime() : p.time;
              return [time, p.dopplerShift];
            }),
            itemStyle: { color: ASTRO_DATA_VIZ_COLORS.mixed[2] },
            symbolSize: 4,
          });
        }

        // Elevation overlay
        if (showElevation && predicted.some((p) => p.elevation !== undefined)) {
          series.push({
            name: "Elevation",
            type: "line",
            yAxisIndex: 1,
            data: predicted
              .filter((p) => p.elevation !== undefined)
              .map((p) => {
                const time = p.time instanceof Date ? p.time.getTime() : p.time;
                return [time, p.elevation];
              }),
            lineStyle: {
              color: SOFT_STATUS_COLORS.normal,
              width: 1,
              type: "dashed",
            },
            symbol: "none",
            areaStyle: { color: `${STATUS_COLORS.normal}20` },
          });
        }

        return {
          tooltip: {
            show: true,
            trigger: "axis",
            ...tooltipStyle,
            formatter: (params: unknown): string => {
              if (!Array.isArray(params) || params.length === 0) return "";
              const firstParam = params[0] as any;
              const time = new Date(firstParam.data[0]).toLocaleTimeString();
              let html = `<div style="font-weight:600;margin-bottom:4px">${time}</div>`;
              params.forEach((p: any) => {
                const value =
                  p.seriesName === "Elevation"
                    ? `${p.data[1]?.toFixed(1)}°`
                    : `${(p.data[1] / 1000).toFixed(2)} kHz`;
                html += `<div>${p.marker} ${p.seriesName}: ${value}</div>`;
              });
              return html;
            },
          },
          legend: { show: true, top: 10 },
          grid: { left: 60, right: 60, top: 50, bottom: 40 },
          xAxis: { type: "time", name: "Time" },
          yAxis: [
            {
              type: "value",
              name: "Doppler (kHz)",
              axisLabel: { formatter: (v: number) => (v / 1000).toFixed(1) },
            },
            {
              type: "value",
              name: "Elevation (°)",
              position: "right",
              min: 0,
              max: 90,
              show: showElevation,
            },
          ],
          series,
        };
      }, [predicted, measured, showResiduals, showElevation, tooltipStyle]);

      return (
        <AstroChart
          ref={ref}
          type="line"
          title={chartTitle}
          series={[]}
          width={width}
          height={height}
          loading={loading}
          className={className}
          echartsOptions={echartsOptions as any}
          export={exportOptions}
          infoTooltip={infoTooltip}
        />
      );
    },
  ),
);

// =============================================================================
// LinkMarginChart - Link Budget Margin Analysis
// =============================================================================

export interface LinkMarginChartProps extends CommonChartProps {
  /** Link margin data points */
  data: LinkMarginPoint[];
  /** Minimum acceptable margin (dB) */
  minMargin: number;
  /** Show uplink, downlink, or both */
  mode?: "uplink" | "downlink" | "both";
  /** Show rain fade overlay */
  showRainFade?: boolean;
  title?: string;
  width?: number | string;
  height?: number;
  loading?: boolean;
  className?: string;
}

/**
 * LinkMarginChart - Link budget margin visualization
 *
 * USE CASES:
 * - Link budget analysis
 * - Fade margin planning
 * - Antenna sizing decisions
 * - Coverage boundary analysis
 */
export const LinkMarginChart = memo(
  forwardRef<AstroChartHandle, LinkMarginChartProps>(function LinkMarginChart(
    {
      data = [],
      minMargin,
      mode = "both",
      showRainFade: _showRainFade = false,
      title = "Link Margin vs Elevation",
      width = "100%",
      height = 250,
      loading = false,
      className = "",
      export: exportOptions,
      infoTooltip,
    },
    ref,
  ) {
    const series: SeriesData[] = [];

    if (
      (mode === "uplink" || mode === "both") &&
      data.some((d) => d.uplinkMargin !== undefined)
    ) {
      series.push({
        id: "uplink",
        name: "Uplink Margin",
        data: data
          .filter((d) => d.uplinkMargin !== undefined)
          .map((d) => ({ x: d.elevation, y: d.uplinkMargin! })),
        color: ASTRO_DATA_VIZ_COLORS.mixed[0],
      });
    }

    if (
      (mode === "downlink" || mode === "both") &&
      data.some((d) => d.downlinkMargin !== undefined)
    ) {
      series.push({
        id: "downlink",
        name: "Downlink Margin",
        data: data
          .filter((d) => d.downlinkMargin !== undefined)
          .map((d) => ({ x: d.elevation, y: d.downlinkMargin! })),
        color: ASTRO_DATA_VIZ_COLORS.mixed[2],
      });
    }

    const annotations: AnnotationOptions = {
      markLines: {
        data: [
          {
            yAxis: minMargin,
            label: `Min Margin (${minMargin} dB)`,
            color: SOFT_STATUS_COLORS.caution,
            lineStyle: { type: "dashed" },
          },
        ],
      },
      markAreas: {
        data: [
          [{ yAxis: 0, name: "Insufficient Margin" }, { yAxis: minMargin }],
        ],
        itemStyle: { color: `${STATUS_COLORS.critical}15` },
      },
    };

    return (
      <AstroChart
        ref={ref}
        type="line"
        title={title}
        series={series}
        width={width}
        height={height}
        loading={loading}
        className={className}
        xAxis={{ type: "value", name: "Elevation (°)", min: 0, max: 90 }}
        yAxis={{ type: "value", name: "Margin (dB)" }}
        tooltip={{ trigger: "axis", crosshair: "x" }}
        legend={{ show: mode === "both", position: "bottom" }}
        annotations={annotations}
        export={exportOptions}
        infoTooltip={infoTooltip}
      />
    );
  }),
);

// =============================================================================
// ConstellationCoverageChart - Global Coverage Heatmap
// =============================================================================

export interface ConstellationCoverageChartProps extends CommonChartProps {
  /** Coverage grid data (lat × lon) */
  data: CoverageGridData;
  /** Chart title */
  title?: string;
  /** Coverage metric label */
  metricLabel?: string;
  /** Coverage metric unit */
  metricUnit?: string;
  /** Color map: viridis, jet, plasma, thermal */
  colorMap?: "viridis" | "jet" | "plasma" | "thermal";
  /** Value range [min, max] */
  valueRange?: [number, number];
  /** Show satellite positions */
  satellites?: Array<{
    name: string;
    lat: number;
    lon: number;
    color?: string;
  }>;
  /** Show ground stations */
  groundStations?: Array<{ name: string; lat: number; lon: number }>;
  width?: number | string;
  height?: number;
  loading?: boolean;
  className?: string;
}

/**
 * ConstellationCoverageChart - Global constellation coverage visualization
 *
 * USE CASES:
 * - Revisit time analysis
 * - Coverage gap identification
 * - Link budget mapping
 * - Latency analysis for LEO constellations
 * - Service availability assessment
 */
export const ConstellationCoverageChart = memo(
  forwardRef<AstroChartHandle, ConstellationCoverageChartProps>(
    function ConstellationCoverageChart(
      {
        data = { coverage: [], latitudes: [], longitudes: [] },
        title = "Constellation Coverage",
        metricLabel = "Coverage",
        metricUnit = "%",
        colorMap = "viridis",
        valueRange,
        satellites = [],
        groundStations = [],
        width = "100%",
        height = 400,
        loading = false,
        className = "",
        export: exportOptions,
        infoTooltip,
      },
      ref,
    ) {
      const { tokens } = useTheme();
      const tooltipStyle = useChartTooltipStyle();
      const labelPrimary = tokens.colors.text.primary;
      const labelSecondary = tokens.colors.text.secondary;

      const colorMaps: Record<string, string[]> = {
        viridis: [
          "#440154",
          "#482777",
          "#3e4989",
          "#31688e",
          "#26828e",
          "#1f9e89",
          "#35b779",
          "#6ece58",
          "#b5de2b",
          "#fde725",
        ],
        jet: [
          "#00007f",
          "#0000ff",
          "#007fff",
          "#00ffff",
          "#7fff7f",
          "#ffff00",
          "#ff7f00",
          "#ff0000",
          "#7f0000",
        ],
        plasma: [
          "#0d0887",
          "#46039f",
          "#7201a8",
          "#9c179e",
          "#bd3786",
          "#d8576b",
          "#ed7953",
          "#fb9f3a",
          "#fdca26",
          "#f0f921",
        ],
        thermal: [
          "#000000",
          "#1a0a2e",
          "#3d1551",
          "#6b1d6a",
          "#a12568",
          "#d3405c",
          "#eb6841",
          "#f5a623",
          "#f7dc6f",
          "#ffffff",
        ],
      };

      const echartsOptions = useMemo(() => {
        if (
          !data.coverage.length ||
          !data.latitudes.length ||
          !data.longitudes.length
        )
          return {};

        // Build heatmap data: [lonIdx, latIdx, value]
        const heatmapData: [number, number, number][] = [];
        let minVal = Infinity;
        let maxVal = -Infinity;

        data.latitudes.forEach((_, latIdx) => {
          data.longitudes.forEach((_, lonIdx) => {
            const val = data.coverage[latIdx]?.[lonIdx] ?? 0;
            heatmapData.push([lonIdx, latIdx, val]);
            if (val < minVal) minVal = val;
            if (val > maxVal) maxVal = val;
          });
        });

        const range = valueRange || [minVal, maxVal];

        const lonLabels = data.longitudes.map((v) => `${v.toFixed(0)}°`);
        const latLabels = data.latitudes.map((v) => `${v.toFixed(0)}°`);

        const seriesArray: any[] = [
          {
            type: "heatmap",
            data: heatmapData,
            emphasis: { itemStyle: { borderColor: "#fff", borderWidth: 1 } },
            progressive: 5000,
          },
        ];

        // Overlay satellite positions as scatter
        if (satellites.length > 0) {
          seriesArray.push({
            type: "scatter",
            name: "Satellites",
            coordinateSystem: "cartesian2d",
            data: satellites.map((sat) => {
              const lonIdx = data.longitudes.findIndex(
                (v) =>
                  Math.abs(v - sat.lon) <
                  data.longitudes[1] - data.longitudes[0],
              );
              const latIdx = data.latitudes.findIndex(
                (v) =>
                  Math.abs(v - sat.lat) < data.latitudes[1] - data.latitudes[0],
              );
              return {
                value: [lonIdx >= 0 ? lonIdx : 0, latIdx >= 0 ? latIdx : 0],
                name: sat.name,
                itemStyle: { color: sat.color || "#fff" },
              };
            }),
            symbolSize: 8,
            symbol: "triangle",
            z: 10,
            label: {
              show: true,
              formatter: (p: any) => p.data?.name || "",
              position: "top",
              color: labelPrimary,
              fontSize: 9,
            },
          });
        }

        // Overlay ground stations
        if (groundStations.length > 0) {
          seriesArray.push({
            type: "scatter",
            name: "Ground Stations",
            coordinateSystem: "cartesian2d",
            data: groundStations.map((gs) => {
              const lonIdx = data.longitudes.findIndex(
                (v) =>
                  Math.abs(v - gs.lon) <
                  data.longitudes[1] - data.longitudes[0],
              );
              const latIdx = data.latitudes.findIndex(
                (v) =>
                  Math.abs(v - gs.lat) < data.latitudes[1] - data.latitudes[0],
              );
              return {
                value: [lonIdx >= 0 ? lonIdx : 0, latIdx >= 0 ? latIdx : 0],
                name: gs.name,
              };
            }),
            symbolSize: 10,
            symbol: "diamond",
            itemStyle: { color: ASTRO_DATA_VIZ_COLORS.mixed[2] },
            z: 10,
            label: {
              show: true,
              formatter: (p: any) => p.data?.name || "",
              position: "bottom",
              color: labelSecondary,
              fontSize: 9,
            },
          });
        }

        return {
          tooltip: {
            show: true,
            ...tooltipStyle,
            formatter: (params: any): string => {
              if (Array.isArray(params)) return "";
              const d = params.data;
              if (params.seriesType === "scatter")
                return `<div style="font-weight:600">${d?.name || ""}</div>`;
              const [lonIdx, latIdx, val] = d || [];
              const lon = data.longitudes[lonIdx] ?? "N/A";
              const lat = data.latitudes[latIdx] ?? "N/A";
              return `<div style="font-weight:600">${metricLabel}</div>
              <div>Lat: ${typeof lat === "number" ? lat.toFixed(1) : lat}°</div>
              <div>Lon: ${typeof lon === "number" ? lon.toFixed(1) : lon}°</div>
              <div>${metricLabel}: ${val?.toFixed(1) ?? "N/A"} ${metricUnit}</div>`;
            },
          },
          grid: { left: 60, right: 50, top: 40, bottom: 80 },
          xAxis: {
            type: "category",
            data: lonLabels,
            name: "Longitude",
            nameLocation: "middle",
            nameGap: 30,
            axisLabel: { interval: Math.floor(lonLabels.length / 12) },
          },
          yAxis: {
            type: "category",
            data: latLabels,
            name: "Latitude",
            nameLocation: "middle",
            nameGap: 40,
            axisLabel: { interval: Math.floor(latLabels.length / 6) },
          },
          visualMap: {
            min: range[0],
            max: range[1],
            calculable: true,
            orient: "horizontal",
            left: "center",
            bottom: 0,
            hoverLink: true,
            outOfRange: { color: "rgba(128, 128, 128, 0.12)", opacity: 0.15 },
            inRange: { color: colorMaps[colorMap] || colorMaps.viridis },
            text: [`${range[1]} ${metricUnit}`, `${range[0]} ${metricUnit}`],
            textStyle: { color: labelSecondary },
          },
          series: seriesArray,
        };
      }, [
        data,
        satellites,
        groundStations,
        colorMap,
        valueRange,
        metricLabel,
        metricUnit,
        tooltipStyle,
        labelPrimary,
        labelSecondary,
      ]);

      return (
        <AstroChart
          ref={ref}
          type="heatmap"
          series={[]}
          title={title}
          width={width}
          height={height}
          loading={loading}
          className={className}
          echartsOptions={echartsOptions as any}
          export={exportOptions}
          infoTooltip={infoTooltip}
        />
      );
    },
  ),
);

// =============================================================================
// ConjunctionChart - 2D Close Approach Assessment
// =============================================================================

export interface ConjunctionChartProps extends CommonChartProps {
  /** Conjunction events */
  events: ConjunctionEvent[];
  /** Time range [start, end] in ms */
  timeRange?: [number, number];
  /** Miss distance threshold for warning (km) */
  warningThreshold?: number;
  /** Miss distance threshold for critical (km) */
  criticalThreshold?: number;
  /** Minimum probability to display */
  minProbability?: number;
  /** Show probability on secondary axis */
  showProbability?: boolean;
  title?: string;
  width?: number | string;
  height?: number;
  loading?: boolean;
  className?: string;
}

/**
 * ConjunctionChart - 2D conjunction/collision assessment timeline
 *
 * USE CASES:
 * - Conjunction screening timeline
 * - Collision avoidance decision support
 * - Debris close approach history
 * - Maneuver planning trigger assessment
 */
export const ConjunctionChart = memo(
  forwardRef<AstroChartHandle, ConjunctionChartProps>(function ConjunctionChart(
    {
      events = [],
      timeRange,
      warningThreshold = 5,
      criticalThreshold = 1,
      minProbability: _minProbability,
      showProbability = true,
      title = "Conjunction Assessment",
      width = "100%",
      height = 350,
      loading = false,
      className = "",
      export: exportOptions,
      infoTooltip,
    },
    ref,
  ) {
    const tooltipStyle = useChartTooltipStyle();

    const echartsOptions = useMemo(() => {
      if (!events.length) return {};

      // Sort by TCA
      const sorted = [...events].sort((a, b) => a.tca - b.tca);

      // Miss distance scatter
      const missData = sorted.map((evt) => {
        const color =
          evt.missDistance < criticalThreshold
            ? STATUS_COLORS.critical
            : evt.missDistance < warningThreshold
              ? STATUS_COLORS.caution
              : STATUS_COLORS.normal;
        return {
          value: [evt.tca, evt.missDistance],
          itemStyle: { color },
          symbolSize: Math.max(6, Math.min(20, 22 - evt.missDistance * 2)),
          ...evt,
        };
      });

      const seriesArray: any[] = [
        {
          name: "Miss Distance",
          type: "scatter",
          data: missData,
          symbolSize: (val: any) => val.symbolSize || 10,
          z: 10,
        },
      ];

      // Probability bars on secondary axis
      if (showProbability && sorted.some((e) => e.probability !== undefined)) {
        seriesArray.push({
          name: "Probability (Pc)",
          type: "bar",
          yAxisIndex: 1,
          data: sorted.map((evt) => ({
            value: [evt.tca, evt.probability ?? 0],
            itemStyle: {
              color:
                (evt.probability ?? 0) > 1e-4
                  ? `${STATUS_COLORS.critical}99`
                  : (evt.probability ?? 0) > 1e-6
                    ? `${STATUS_COLORS.caution}99`
                    : `${STATUS_COLORS.normal}60`,
            },
          })),
          barWidth: 8,
          z: 5,
        });
      }

      return {
        tooltip: {
          show: true,
          trigger: "item",
          ...tooltipStyle,
          formatter: (params: any): string => {
            const d = params.data || {};
            if (params.seriesName === "Probability (Pc)") {
              return `<div>Pc: ${(d.value?.[1] ?? 0).toExponential(2)}</div>`;
            }
            const mdColor =
              d.missDistance < criticalThreshold
                ? STATUS_COLORS.critical
                : d.missDistance < warningThreshold
                  ? STATUS_COLORS.caution
                  : STATUS_COLORS.normal;
            const tca = new Date(d.value?.[0] ?? d.tca);
            return `<div style="font-weight:600;margin-bottom:4px">${d.primary} × ${d.secondary}</div>
              <div>TCA: ${tca.toLocaleString()}</div>
              <div style="color:${mdColor}">Miss Distance: ${d.missDistance?.toFixed(3)} km</div>
              ${d.probability !== undefined ? `<div>Pc: ${(d.probability * 100).toExponential(2)}%</div>` : ""}
              ${d.relativeVelocity ? `<div>Rel. Velocity: ${d.relativeVelocity.toFixed(2)} km/s</div>` : ""}`;
          },
        },
        legend: { show: showProbability, top: 10 },
        grid: {
          left: 60,
          right: showProbability ? 80 : 40,
          top: 50,
          bottom: 40,
        },
        xAxis: {
          type: "time",
          name: "Time of Closest Approach",
          ...(timeRange ? { min: timeRange[0], max: timeRange[1] } : {}),
        },
        yAxis: [
          {
            type: "value",
            name: "Miss Distance (km)",
            min: 0,
            splitLine: { lineStyle: { color: "rgba(100, 116, 139, 0.15)" } },
          },
          {
            type: "log",
            name: showProbability ? "Probability" : undefined,
            position: "right",
            show: showProbability,
            min: 1e-10,
            max: 1,
            axisLabel: { formatter: (v: number) => v.toExponential(0) },
            splitLine: { show: false },
          },
        ],
        // Threshold marklines
        series: seriesArray.map((s, i) => ({
          ...s,
          ...(i === 0
            ? {
                markLine: {
                  silent: true,
                  data: [
                    {
                      yAxis: criticalThreshold,
                      label: {
                        formatter: `Critical (${criticalThreshold} km)`,
                        color: STATUS_COLORS.critical,
                      },
                      lineStyle: {
                        color: SOFT_STATUS_COLORS.critical,
                        type: "dashed",
                        width: 1,
                      },
                    },
                    {
                      yAxis: warningThreshold,
                      label: {
                        formatter: `Warning (${warningThreshold} km)`,
                        color: STATUS_COLORS.caution,
                      },
                      lineStyle: {
                        color: SOFT_STATUS_COLORS.caution,
                        type: "dashed",
                        width: 1,
                      },
                    },
                  ],
                },
                markArea: {
                  silent: true,
                  data: [[{ yAxis: 0 }, { yAxis: criticalThreshold }]],
                  itemStyle: { color: `${STATUS_COLORS.critical}10` },
                },
              }
            : {}),
        })),
      };
    }, [
      events,
      timeRange,
      warningThreshold,
      criticalThreshold,
      showProbability,
      tooltipStyle,
    ]);

    return (
      <AstroChart
        ref={ref}
        type="scatter"
        series={[]}
        title={title}
        width={width}
        height={height}
        loading={loading}
        className={className}
        echartsOptions={echartsOptions as any}
        export={exportOptions}
        infoTooltip={infoTooltip}
      />
    );
  }),
);
